/**
 * What's New — plain-English release notes for everyday users.
 *
 * This is the single source of truth for the What's New page (/home/whats-new),
 * the What's New popup, and the nav bell badge. Everything here is written for
 * the person USING the app, not for engineers — no PR numbers, no file paths,
 * no jargon. If a normal office or field user couldn't read a line and know
 * what changed for them, rewrite it.
 *
 * ── How to publish an update (the "automatic push") ──────────────────────────
 *   1. Add a new WhatsNewRelease to the TOP of WHATS_NEW_RELEASES (newest first).
 *   2. Give it a fresh, unique `id` (we use the ship date, e.g. '2026-07-14').
 *   3. Fill in a few `items`, each tagged 'new' | 'improved' | 'fixed'.
 *   That's it. Because the newest release id changes, every user automatically
 *   sees the "New" dot on the gift bell, and the update pops up once the next
 *   time they open the app. No deploy toggle, no per-user work.
 *
 * Keep each release to a handful of the things people will actually notice.
 * Small internal/plumbing changes don't belong here.
 */

export type WhatsNewType = 'new' | 'improved' | 'fixed';

export interface WhatsNewItem {
  /** What kind of change this is — drives the coloured chip + icon. */
  type: WhatsNewType;
  /** Short headline, sentence case, no trailing full stop. */
  title: string;
  /** One or two plain sentences on what it means for the user. */
  description: string;
}

export interface WhatsNewRelease {
  /** Stable, unique id. We use the ship date (YYYY-MM-DD). */
  id: string;
  /** Display date (YYYY-MM-DD). Rendered in the user's locale. */
  date: string;
  /** Short, friendly headline for the whole release. */
  title: string;
  /** Optional one-liner shown under the title. */
  tagline?: string;
  /** The changes, most interesting first. */
  items: WhatsNewItem[];
}

/**
 * Releases, NEWEST FIRST. The first entry is treated as "the latest".
 */
export const WHATS_NEW_RELEASES: WhatsNewRelease[] = [
  // autobuild:whats-new — the ship step (scripts/autobuild/compile-changelog.mjs)
  // inserts each shipped request's release here, so it is always the newest.
  {
    id: '2026-11-05-proposals-one-click-signing',
    date: '2026-11-05',
    title: 'One-click signing and a cleaner customer page',
    tagline: 'In the new proposal builder.',
    items: [
      {
        type: 'new',
        title: 'Customers sign in one click',
        description:
          'No more tick boxes. The words right above "Sign & accept" say that clicking it means they agree. The dialog and the thank-you page show your proposal\'s title and the option they chose.',
      },
      {
        type: 'improved',
        title: 'The customer\'s page reads like a proper document',
        description:
          'Proper margins on every screen, option cards they can click, and the browser tab names your business. Preview as customer lets you flip between options too.',
      },
      {
        type: 'fixed',
        title: 'A signed proposal always finishes — or tells you',
        description:
          'If moving the deal to Won doesn\'t finish, the deal says so and admins get a Retry button. "View signed proposal" shows the whole proposal as signed, with the chosen option.',
      },
      {
        type: 'improved',
        title: 'Smaller fixes in the builder and on the deal',
        description:
          'Summary / Itemised is back in Simple view, restoring a version resets everything, and typing straight after a click replaces the text. The signature on the deal is readable, the old Better Proposals card says it\'s been replaced, and saved versions no longer flood the timeline.',
      },
    ],
  },
  {
    id: '2026-11-04-proposal-plans-per-option-and-own-sections',
    date: '2026-11-04',
    title: 'A plan for each option, and sections of your own',
    tagline: 'In the new proposal builder.',
    items: [
      {
        type: 'new',
        title: 'Each option can have its own monthly plan',
        description:
          'Tag a monthly plan to an option, or use "A plan per option" on the plan\'s menu. Each option card now reads "$X upfront + $Y / month", and the customer\'s page follows the option they pick.',
      },
      {
        type: 'fixed',
        title: 'The plan the customer signs is the plan that starts',
        description:
          'When a customer accepts an option, its monthly plan is the one set up when the job is done — not whichever plan was picked last in the builder.',
      },
      {
        type: 'new',
        title: 'Add your own sections',
        description:
          '"+ Add section" → "Your own section…" adds a section with a title, text and an image — for "Who we are", testimonials, anything. Move it up or down the page. Templates keep them.',
      },
    ],
  },
  {
    id: '2026-11-04-a-simpler-proposal-builder',
    date: '2026-11-04',
    title: 'A simpler proposal builder',
    tagline: 'In the new proposal builder.',
    items: [
      {
        type: 'improved',
        title: 'Simple view by default',
        description:
          'The builder opens in Simple view: fewer buttons, the same proposal. Anything a template or you already used still shows. Switch to All tools at the top for everything else.',
      },
      {
        type: 'improved',
        title: 'Only the sections the customer will see',
        description:
          'Empty and hidden sections no longer take up room while you build. Add one with "+ Add section" under the last section. A section the customer can\'t see yet says so.',
      },
      {
        type: 'new',
        title: 'See where the customer signs',
        description:
          'Preview as customer now shows the acceptance block the customer signs, and the customer\'s page has an Accept button right there too.',
      },
      {
        type: 'new',
        title: 'Duplicate an option',
        description: 'Each option\'s menu has Duplicate. Three options now sit side by side in one row.',
      },
      {
        type: 'fixed',
        title: 'Typing behaves',
        description:
          'Typing "/" in a field no longer opens search, Select all works on the first click, and single line breaks in the welcome letter stay as line breaks.',
      },
      {
        type: 'improved',
        title: 'Tidier wording for the customer',
        description:
          'Product cards use the part\'s short "Why we chose this" (or its first sentence) instead of the whole catalogue text. Monthly plans read "Up to 100 Mbps download" and can have a customer note. Labour no longer says "installation" twice, and hero chips can be edited.',
      },
    ],
  },
  {
    id: '2026-10-10-job-addresses-always-show-the-town',
    date: '2026-10-10',
    title: 'Job addresses always show the town',
    items: [
      { type: 'fixed', title: 'Job addresses always show the town', description: 'Job cards, the weather line, Tomorrow and the team list now show the full address — street, town, state and postcode — the same way every time. Fault consult cards show the whole address too.' },
    ],
  },
  {
    id: '2026-10-10-completed-jobs-always-show-time-on-site',
    date: '2026-10-10',
    title: 'Completed jobs always show time on site',
    items: [
      { type: 'improved', title: 'Completed jobs always show time on site', description: 'If the tech didn\'t start the timer, the Job Completed card now says "No time recorded" and shows the booked hours. Tap Set time to enter the real hours, so every completed job has a figure to bill from.' },
    ],
  },
  {
    id: '2026-10-09-proposal-builder-terms-and-drafting',
    date: '2026-10-09',
    title: 'The right terms on every proposal, and drafts that read well',
    tagline: 'In the new proposal builder.',
    items: [
      {
        type: 'fixed',
        title: 'Starlink terms stay on Starlink proposals',
        description:
          'In the terms library, tag a clause with the kinds of job it is for (Starlink, NBN, monthly plans and so on). Untagged clauses go on every proposal; tagged ones only where they belong. The checklist warns if the terms still mention something the proposal does not include.',
      },
      {
        type: 'improved',
        title: 'Drafts follow the house rules',
        description:
          "Draft for me writes the customer's name properly (\"Hi Eliza,\", not \"Hi ELIZA,\"), says \"about a day on site\" instead of hours or how many technicians, and skips filler points.",
      },
      {
        type: 'new',
        title: 'One click drafts the whole proposal',
        description:
          'A new proposal with its areas in place offers "Draft all empty blocks" at the top. One Undo takes the whole draft back. Feature this product now writes the card for the customer straight away.',
      },
      {
        type: 'new',
        title: 'More checks before you send',
        description:
          'The checklist now flags hours or headcount in customer text (with a one-click Redraft), custom lines with no cost, and the customer\'s name in capitals.',
      },
    ],
  },
  {
    id: '2026-10-09-proposal-builder-typing',
    date: '2026-10-09',
    title: 'Pricing a proposal no longer fights the keyboard',
    tagline: 'In the new proposal builder.',
    items: [
      {
        type: 'fixed',
        title: 'Enter adds the part you can see',
        description:
          'Type a product and press Enter to add the highlighted match. A "Create custom line" row at the bottom of the list adds what you typed instead, and an empty search says so.',
      },
      {
        type: 'fixed',
        title: 'Custom, Charge and Allowance keep what you typed',
        description: 'Type "Consumables", click Charge, and the charge line is already named Consumables, ready for its price.',
      },
      {
        type: 'fixed',
        title: 'Tab goes name, quantity, price',
        description: 'Long product names are no longer cut off while you edit them, and Escape keeps a point you were typing instead of deleting it.',
      },
      {
        type: 'fixed',
        title: 'The first click always works',
        description: 'Add point, Add area and the other buttons act on the first click, even while you are still typing in another box.',
      },
      {
        type: 'improved',
        title: 'A price you type stays the price you typed',
        description: 'Type $500 and the box keeps showing $500.00. Option cards now show cents whenever the price table does, so they always match.',
      },
      {
        type: 'improved',
        title: 'Faster parts library',
        description: 'Browse all and Replace from catalog search as you type. Replace opens already searched for the line, and you pick the quantity on the row.',
      },
    ],
  },
  {
    id: '2026-10-09-proposal-builder-easier-to-read',
    date: '2026-10-09',
    title: 'Proposals your customer can read at a glance',
    tagline: 'In the new proposal builder.',
    items: [
      {
        type: 'improved',
        title: 'One price per area',
        description:
          'Each area shows the customer one line: what is in it and its price. Switch an area to Groups or Itemised when they want more detail.',
      },
      {
        type: 'improved',
        title: 'Prices the way your customer thinks',
        description:
          'Home owners see every price including GST, and the total including GST comes first. Businesses still see prices excluding GST.',
      },
      {
        type: 'fixed',
        title: 'Optional extras stay optional',
        description:
          'A line that starts unticked is left out of every total and the deal value. The customer sees a tick box and "+ $X if added".',
      },
      {
        type: 'improved',
        title: 'The price comes first',
        description:
          'After the welcome letter comes the price. What is involved, how the install runs and Good to know follow, before the terms.',
      },
    ],
  },
  {
    id: '2026-10-10-customer-phone-now-shows-on-the-schedule',
    date: '2026-10-10',
    title: 'Customer phone now shows on the Schedule',
    items: [
      { type: 'fixed', title: 'Customer phone now shows on the Schedule', description: 'Open any job on the Schedule and you\'ll see a phone number to tap or copy — the customer\'s, if the job doesn\'t have its own. Fault jobs included.' },
    ],
  },
  {
    id: '2026-10-09-settings-and-admin-tidy-up-for-tenant-security',
    date: '2026-10-09',
    title: 'Settings and Admin tidy-up ahead of tighter data security',
    items: [
      { type: 'improved', title: 'Company Profile form removed from Settings', description: 'Settings → General no longer shows the old Company Profile form. Nothing used what it saved — your company name, ABN, tax rate and branding come from your business profile and have not changed.' },
      { type: 'improved', title: 'Xero and Stripe pages are for admins only', description: 'Only administrators see the Xero and Stripe tiles in Admin now. Anyone else who opens those pages sees a short note instead of the connection details.' },
      { type: 'improved', title: 'Admin data tools only touch your own business', description: 'The launch data wipe in Settings → General now only ever deletes your own business\'s records. The old TenantId Backfill button is gone — Tenant Tag Check & Fix in Dev Tools does that job.' },
    ],
  },
  {
    id: '2026-10-09-clear-notifications-from-the-bell-on-you',
    date: '2026-10-09',
    title: 'Clear notifications from the bell on your phone',
    items: [
      { type: 'fixed', title: 'Clear notifications from the bell on your phone', description: 'On a phone the notifications panel now fits on the screen, and the X to clear each one is bigger and easier to tap.' },
    ],
  },
  {
    id: '2026-10-08-proposal-builder',
    date: '2026-10-08',
    title: 'A new proposal builder',
    tagline: 'Opt-in for now: open a deal and press "Open proposal builder".',
    items: [
      {
        type: 'new',
        title: 'A new proposal builder',
        description:
          'Add an area like "Front door", type the products into it, and keep going. You see the customer’s document as you build it, with no pop-ups in the way.',
      },
      {
        type: 'new',
        title: 'Give customers a choice',
        description:
          'Offer Option A and Option B side by side, each with its own price column. The customer picks one when they sign. Before you mark a deal won by hand, lock the option they chose.',
      },
      {
        type: 'improved',
        title: 'Grouped pricing',
        description:
          'Each area shows short summary lines, like "41 turret cameras", instead of every part. Switch an area to itemised when the customer wants the full list.',
      },
      {
        type: 'new',
        title: 'Start from a template',
        description: 'A Starlink proposal is one click: the kit, the install, the monthly plan and all the wording are filled in for you.',
      },
      {
        type: 'new',
        title: 'Draft for me',
        description: 'Ask for a first draft of any block of text from the items on the page. Change whatever you like before you send.',
      },
      {
        type: 'new',
        title: 'Starlink plans follow your plan prices',
        description:
          'Switch a proposal between Starlink plans in one click. Plan prices are only changed in Settings → Plans, so every proposal uses the same figures.',
      },
      {
        type: 'improved',
        title: 'Version history',
        description: 'Save a version whenever you like and restore any earlier one. Every proposal you send is saved as a version automatically.',
      },
    ],
  },
  {
    id: '2026-10-09-tentative-holds-can-now-cover-several-da',
    date: '2026-10-09',
    title: 'Tentative holds can now cover several days',
    items: [
      { type: 'improved', title: 'Tentative holds can now cover several days', description: 'When you add a Tentative event on the Schedule, pick a start and end date and Theo holds every weekday in between in one go. Open any day to change or delete just that day or the whole hold.' },
    ],
  },
  {
    id: '2026-10-09-click-the-schedule-to-add-an-event',
    date: '2026-10-09',
    title: 'Click the schedule to add an event',
    items: [
      { type: 'new', title: 'Click the schedule to add an event', description: 'On the Schedule, click an empty spot in a tech\'s column and pick the kind of event. The Add Event form opens with the tech, day and time already filled in.' },
    ],
  },
  {
    id: '2026-10-09-account-closure-requests-that-alert-bill',
    date: '2026-10-09',
    title: 'Account Closure requests that alert billing, provisioning and customer service',
    items: [
      { type: 'new', title: 'Account Closure requests that alert billing, provisioning and customer service', description: 'Pick "Account Closure" on a new service request, choose who handles billing, provisioning and the customer, and they each get an alert. The request shows a checklist so you can see which parts are done.' },
    ],
  },
  {
    id: '2026-10-09-booking-status-and-filters-on-the-schedu',
    date: '2026-10-09',
    title: 'Booking status and filters on the schedule queue',
    items: [
      { type: 'new', title: 'Booking status and filters on the schedule queue', description: 'Mark a queued job as In progress, Tentative, No answer or Held by customer, with a note of what you did, and filter the queue by status. A Tentative job can go on the calendar to save the spot and stays marked until you confirm it.' },
    ],
  },
  {
    id: '2026-10-09-undo-a-customer-s-confirmed-or-no-answer',
    date: '2026-10-09',
    title: 'Undo a customer\'s Confirmed or No Answer mark on the Schedule',
    items: [
      { type: 'fixed', title: 'Undo a customer\'s Confirmed or No Answer mark on the Schedule', description: 'Right-click a job and pick "Clear contact status", or press "Clear" on a queue card. The job and its deal go back to Not contacted, and nothing is sent to the customer.' },
    ],
  },
  {
    id: '2026-10-09-ordered-now-shows-in-provisioning-progre',
    date: '2026-10-09',
    title: 'Ordered now shows in provisioning progress',
    items: [
      { type: 'improved', title: 'Ordered now shows in provisioning progress', description: 'The status bar on the Provisioning page now shows every step, with Ordered right after Awaiting Tech. In the Schedule and Jobs drawers, Ordered items are indigo instead of Not Ordered red.' },
    ],
  },
  {
    id: '2026-10-09-edit-a-service-request-s-billing-lines-a',
    date: '2026-10-09',
    title: 'Edit a service request\'s billing lines after sending',
    items: [
      { type: 'improved', title: 'Edit a service request\'s billing lines after sending', description: 'The Ready to bill card on a service request now shows the lines you sent, with an Edit lines button to change, add or remove them until it\'s invoiced. View in Billing takes you straight to where it lands.' },
    ],
  },
  {
    id: '2026-10-08-add-the-starlink-email-when-the-kit-arri',
    date: '2026-10-08',
    title: 'Add the Starlink email when the kit arrives',
    items: [
      { type: 'new', title: 'Add the Starlink email when the kit arrives', description: 'On Provisioning, a Starlink kit that\'s At Office now has a Starlink Email box next to Kit Number and Account Number, and the "kit arrived" pop-up asks for it too. Leaving it blank keeps any email already saved.' },
    ],
  },
  {
    id: '2026-10-08-slack-status-for-every-tech',
    date: '2026-10-08',
    title: 'Your Slack status follows your jobs',
    items: [
      { type: 'new', title: 'Slack status from your schedule, for every tech', description: 'When you are on a job your Slack status shows 🔧 On site; between jobs or driving to the first one it shows 🚗 Between jobs; it clears when your day is done. A status you set yourself in Slack is never changed. Admins pick who it applies to under Admin → Automations → Slack.' },
    ],
  },
  {
    id: '2026-10-08-pinned-notes-stay-at-the-top-of-the-deal',
    date: '2026-10-08',
    title: 'Pinned notes stay at the top of the deal',
    items: [
      { type: 'new', title: 'Pinned notes stay at the top of the deal', description: 'Pin a note in a deal\'s Timeline and it now sits in a Pinned box at the top of the deal page until you unpin it. Tap "Show in timeline" to jump to where it was written.' },
    ],
  },
  {
    id: '2026-10-08-right-click-menus-on-the-schedule-always',
    date: '2026-10-08',
    title: 'Right-click menus on the Schedule always fit on screen',
    items: [
      { type: 'fixed', title: 'Right-click menus on the Schedule always fit on screen', description: 'Right-clicking a job or event near the bottom or right edge of the Schedule now shows the whole menu, so Unschedule, Delete and every tech in Add Tech are always reachable.' },
    ],
  },
  {
    id: '2026-10-08-recurring-events-now-show-up-and-stay-ho',
    date: '2026-10-08',
    title: 'Recurring events now show up and stay how you set them',
    items: [
      { type: 'fixed', title: 'Recurring events now show up and stay how you set them', description: 'Changing a recurring event\'s days or dates now updates the schedule, and a recurring event saved with no days can be fixed by opening it from Recurring and picking days. Deleting or moving one occurrence now sticks.' },
    ],
  },
  {
    id: '2026-10-08-move-a-booked-car-service-to-a-new-date',
    date: '2026-10-08',
    title: 'Move a booked car service to a new date',
    items: [
      { type: 'fixed', title: 'Move a booked car service to a new date', description: 'Booked vehicle services now have a Reschedule button on the car\'s Bookings tab. Change the date or time and the 🔧 block on the schedule moves with it — no more cancelling and re-booking.' },
    ],
  },
  {
    id: '2026-10-08-ask-a-tech-to-review-a-job-before-the-vi',
    date: '2026-10-08',
    title: 'Ask a tech to review a job before the visit',
    items: [
      { type: 'new', title: 'Ask a tech to review a job before the visit', description: 'On the schedule, open a job and tap "Ask tech to review" with an optional note. The tech gets a push and a banner on the job, and you\'ll see "Reviewed by …" once they\'ve looked.' },
    ],
  },
  {
    id: '2026-10-08-import-public-holidays-onto-the-schedule',
    date: '2026-10-08',
    title: 'Import public holidays onto the Schedule',
    items: [
      { type: 'new', title: 'Import public holidays onto the Schedule', description: 'Press Import holidays on the Schedule to add the year\'s public holidays for your state to every tech in one go. Techs show as unavailable those days, and Theo checks before you book a job onto one.' },
    ],
  },
  {
    id: '2026-10-08-deals-with-no-items-now-show-on-the-prov',
    date: '2026-10-08',
    title: 'Deals with no items now show on the Provisioning board',
    items: [
      { type: 'fixed', title: 'Deals with no items now show on the Provisioning board', description: 'If you move a deal to Provisioning before it has any line items, it now appears on the Hardware tab marked "No items yet". Open it to add items on the deal or clear it from the queue.' },
    ],
  },
  {
    id: '2026-10-08-change-a-job-s-type-after-it-s-booked',
    date: '2026-10-08',
    title: 'Change a job\'s type after it\'s booked',
    items: [
      { type: 'new', title: 'Change a job\'s type after it\'s booked', description: 'On Jobs, tap the type at the top of a job (e.g. TASK) to switch it to Install, Fault, Pre-Site or Task. Jobs from a deal or a fault, and completed jobs, can\'t be changed.' },
    ],
  },
  {
    id: '2026-10-08-every-service-request-and-fault-now-has',
    date: '2026-10-08',
    title: 'Every service request and fault now has a ticket number',
    items: [
      { type: 'new', title: 'Every service request and fault now has a ticket number', description: 'New service requests get a number like SR-0042 and faults like FLT-0017. You\'ll see it on the card and at the top of the ticket — tap it to copy it into Slack — and you can type it into search to find the ticket.' },
    ],
  },
  {
    id: '2026-10-08-tracking-details-when-marking-gear-shipp',
    date: '2026-10-08',
    title: 'Tracking details when marking gear shipped',
    items: [
      { type: 'new', title: 'Tracking details when marking gear shipped', description: 'When you mark an item as Shipped on the Provisioning page you can now add the courier and tracking number, or paste a tracking link. It shows on the item card, you can change it later, and a link becomes a Track button.' },
    ],
  },
  {
    id: '2026-10-06-add-screenshots-from-your-photo-library',
    date: '2026-10-06',
    title: 'Add screenshots from your photo library to bug reports',
    items: [
      { type: 'improved', title: 'Add screenshots from your photo library to bug reports', description: 'When you report a bug or idea, you can now pick screenshots straight from your photos with "From library", as well as take a new photo with the camera.' },
    ],
  },
  {
    id: '2026-10-06-autobuild-thread',
    date: '2026-10-06',
    title: 'Talk to the build',
    items: [
      {
        type: 'new',
        title: 'Your report has a thread',
        description: 'Under a report, the Autobuild panel is now a conversation. Post anything the build should know; it reads the thread before each step and replies with what it understood and what it is about to do.',
      },
      {
        type: 'new',
        title: 'Redirect a build',
        description: 'Directors can stop a build that has the wrong idea and point it at what it should do instead — on the report, in Slack, or from Claude. The new spec starts from your note.',
      },
      {
        type: 'improved',
        title: 'Every build message says what it is about',
        description: 'The building, question and approval messages carry a one-line Scope, so you can tell at a glance whether the build has understood.',
      },
    ],
  },
  {
    id: '2026-10-06-clearing-an-action-item-clears-it-from-e',
    date: '2026-10-06',
    title: 'Clearing an action item clears it from everyone\'s plate',
    items: [
      { type: 'improved', title: 'Clearing an action item clears it from everyone\'s plate', description: 'When you mark an action item as actioned, the "Tagged in" pings it sent drop off everyone\'s On Your Plate and bell automatically.' },
    ],
  },
  {
    id: '2026-10-06-usage',
    date: '2026-10-06',
    title: 'Theo now knows which screens get used — and which don\'t',
    tagline: 'Owners get a usage page; your reports now carry what you did just before.',
    items: [
      {
        type: 'new',
        title: 'A usage page for owners',
        description:
          'Admin → Usage shows which screens and buttons each role uses most and least, where the errors and the reports are, and whether a change got used after it shipped. Nothing you type is ever recorded.',
      },
      {
        type: 'improved',
        title: 'Reports carry the steps before the problem',
        description:
          'When you send "Report an issue", the last few screens and taps of your session go with it, so the fix starts from what actually happened — not from what you remember.',
      },
    ],
  },
  {
    id: '2026-10-05-autobuild-approvals',
    date: '2026-10-05',
    title: 'Reports can now be built for you — with a director\'s say-so',
    tagline: 'Answer its questions and approve builds from the report, your Plate, or Claude.',
    items: [
      {
        type: 'new',
        title: 'A build assistant works the "Report an issue" queue',
        description:
          'New reports can be picked up, built and tested automatically. Open the report and you\'ll see a timeline of what it has done so far, and you get a bell at every step that needs you.',
      },
      {
        type: 'new',
        title: 'It asks when it is unsure',
        description:
          "If it needs to know something, the question lands on the reporter's Plate and on the report itself. Type the answer there and the build carries on.",
      },
      {
        type: 'new',
        title: 'Directors approve before anything goes live',
        description:
          'A finished build waits with before-and-after screenshots. Approve it, or send it back with a note saying what to change — from the report, the Plate task, or Claude.',
      },
    ],
  },
  {
    id: '2026-09-27-claude-bug-reports',
    date: '2026-09-27',
    title: 'Claude can now see bug reports and feature requests',
    tagline: 'Handy for your morning catch-up.',
    items: [
      {
        type: 'new',
        title: 'Bug reports and feature requests in Claude',
        description:
          "If you've connected Claude, it can now read what the team sends with \"Report an issue\" — so it can list the new ones in your daily catch-up, put one on your to-do list, or mark one fixed and let the person who reported it know.",
      },
      {
        type: 'improved',
        title: 'Links open the exact report',
        description:
          'Tapping a "your report was fixed" notification, or a link from Claude, now opens that report on the Bug Reports page instead of the top of the list.',
      },
    ],
  },
  {
    id: '2026-08-26-scheduled-by',
    date: '2026-08-26',
    title: 'You can now see who scheduled a job, and when',
    tagline: 'On the scheduler and on the tech job card.',
    items: [
      {
        type: 'new',
        title: 'Scheduled by, right on the job',
        description:
          "Open a job in the scheduler and you'll see a line like 'Scheduled by Reuben today at 9:23pm' straight under the job details. If it's been moved since, the move gets its own line with who moved it.",
      },
      {
        type: 'new',
        title: 'Techs can see it too',
        description:
          'The same line now shows on the job card in the field, so a tech opening their day can tell whether a job was planned weeks ago or dropped in last night.',
      },
      {
        type: 'new',
        title: 'Last-minute jobs are marked',
        description:
          "Anything that landed in its slot less than 24 hours before it starts gets a 'Short notice' flag on both screens.",
      },
    ],
  },
  {
    id: '2026-08-21-au-dates',
    date: '2026-08-21',
    title: 'Dates and calendars now read the Australian way everywhere',
    tagline: 'Monday-first calendars, day-before-month dates, Sydney time.',
    items: [
      {
        type: 'fixed',
        title: 'A few screens showed American-style dates',
        description:
          "The tech day picker, the tomorrow preview, and the voting screens were printing dates US-style — 'Aug 21' and 'Aug 21, 2026' instead of '21 Aug' and '21 August 2026'. They all read Australian now.",
      },
      {
        type: 'fixed',
        title: 'The red "now" line on the scheduler was 10 hours out',
        description:
          'It was drawn using UTC instead of Sydney time, so at 2:30pm it sat at 4:30am — off the top of the grid, which is why it usually was not there at all. The scheduler also opened scrolled to the wrong hour, and before 10am it highlighted yesterday as today. All fixed.',
      },
      {
        type: 'fixed',
        title: 'The tech calendar started the week on Sunday',
        description:
          'The month calendar in the tech app is now Monday-first, like every other calendar in the app and like a normal Australian week.',
      },
      {
        type: 'fixed',
        title: 'Some dates and times were showing in UK/US format',
        description:
          'Timestamps on checkpoints, fault consults, the MCP connector page, saved bank details and shared cable schedules were formatted using whatever the phone or laptop was set to. They now always use the Australian format.',
      },
      {
        type: 'fixed',
        title: 'Emails and PDFs could be dated a day early',
        description:
          "Job report emails, proposal emails, variation bundle PDFs and the signature audit trail were dated in UTC. Anything generated before about 10am Sydney carried yesterday's date, and the signed proposal trail said 'UTC' instead of AEST. All now stamped Sydney time.",
      },
      {
        type: 'fixed',
        title: "The office dashboard's 'this week' was wrong on Sundays",
        description:
          'On a Sunday it counted the week that was about to start instead of the one finishing that day. Weeks now run Monday to Sunday, always.',
      },
    ],
  },
  {
    id: '2026-08-20-bank-details',
    date: '2026-08-20',
    title: 'Your bank details now live in settings',
    tagline: 'And Theo will not send an invoice until they are filled in.',
    items: [
      {
        type: 'fixed',
        title: 'The bank account on invoices was a placeholder',
        description:
          "The EFT details in the 'Ways to pay' block were written into the code and could not be changed from the app. The account number in there was sample data, not a real account. Every invoice Theo generated carried it. Please check any recent invoice against what you actually banked.",
      },
      {
        type: 'new',
        title: 'Bank details page',
        description:
          'Admin → Billing settings → Bank details. Enter your bank, account name, BSB and account number, and you can see exactly how it will print on the invoice before you save. Admins only.',
      },
      {
        type: 'new',
        title: 'Invoices are blocked until they are set',
        description:
          "Theo will not send an invoice — or raise a deposit — while the bank details are missing or half filled in. The Send button explains why and links you to the page. This is deliberate: an invoice tells a customer where to send money, so it should never go out naming an account that isn't yours.",
      },
    ],
  },
  {
    id: '2026-08-20-invoice-payments',
    date: '2026-08-20',
    title: 'Invoice payments confirm properly now',
    tagline:
      'Card only at the checkout, and nobody gets chased for a payment they have already made.',
    items: [
      {
        type: 'fixed',
        title: 'Customers can no longer accidentally set up a direct debit',
        description:
          "Our Stripe checkout was quietly offering a bank-debit option on one-off invoices. Choosing it set up a direct debit mandate with the customer's bank and took about three business days to clear, so the payment never confirmed on screen and the receipt didn't arrive. The checkout is now card only. Recurring plans still bill through Utilmate exactly as before — that hasn't changed.",
      },
      {
        type: 'improved',
        title: 'Slow payments get an email straight away',
        description:
          "If a payment ever does take days to clear, the customer now gets an email the moment they pay — what they paid, how long it will take, and that we'll send the receipt once it lands. They used to get nothing at all until it cleared.",
      },
      {
        type: 'fixed',
        title: 'No more overdue reminders to people who have paid',
        description:
          'An invoice with a payment still clearing is no longer flipped to overdue or sent a payment reminder.',
      },
      {
        type: 'new',
        title: 'Two new badges on the billing card',
        description:
          '"Payment clearing" shows when a customer has paid but the money is still on its way. "Payment failed" shows when an attempt bounced — worth a call, because they may well think they have paid.',
      },
    ],
  },
  {
    id: '2026-08-16-brand-settings-admins',
    date: '2026-08-16',
    title: 'Brand settings are now admin-only',
    tagline: 'A small tidy-up to who can change how your company looks to customers.',
    items: [
      {
        type: 'improved',
        title: 'Only admins can change brand settings',
        description:
          'Your logo, colours and proposal branding can now only be edited by an admin. Everyone else can still see the settings — there just isn\'t a Save button. Same for the Google review link on the Automations page.',
      },
      {
        type: 'improved',
        title: 'Signing in is a bit safer',
        description:
          'After several wrong PIN attempts in a row, sign-in pauses for 15 minutes. Get your PIN right and nothing changes — it only ever slows down someone guessing.',
      },
    ],
  },
  {
    id: '2026-08-16-leave-by-the-hour',
    date: '2026-08-16',
    title: 'Ask for an hour off, not a whole day',
    tagline:
      'Got a doctor appointment on Tuesday morning? Put in for just that hour.',
    items: [
      {
        type: 'new',
        title: 'Leave requests can now be part of a day',
        description:
          'On Timesheet & Leave, "Sick or annual leave" has a new "Part of a day" option. Pick the date, then the start and finish time — 25 minutes, an hour, whatever you actually need. Shortcut buttons cover 15m, 30m, 1h and 2h, or type any time you like.',
      },
      {
        type: 'improved',
        title: 'Part-day leave blocks out just that window',
        description:
          'A part-day request puts a timed block on your day and on the office scheduler, so nothing gets booked over it — and you stay available for the rest of the day.',
      },
      {
        type: 'improved',
        title: 'The office can add part-day leave for someone else',
        description:
          'Adding leave for a team member from the office Leave page has the same part-of-a-day option.',
      },
    ],
  },
  {
    id: '2026-08-14-billing-looks-the-part',
    date: '2026-08-14',
    title: 'Invoice emails and the payment page got a proper polish',
    tagline:
      'One consistent look from the email to the payment page — and the amount due is always front and centre.',
    items: [
      {
        type: 'improved',
        title: 'Invoice emails lead with the amount',
        description:
          'The amount due, due date and Pay button now sit at the top of every invoice email. The styling holds up in Gmail and Outlook, and looks right on phones.',
      },
      {
        type: 'improved',
        title: 'The email editor preview is now honest',
        description:
          'What you see in the billing email editor is exactly what every customer receives — including customers you never saved a custom template for.',
      },
      {
        type: 'improved',
        title: 'The payment page shows what matters',
        description:
          'The amount due is visible on phones now, overdue invoices say so, and a part-paid invoice shows the balance due — not the full total. Business ABN shows in the footer.',
      },
      {
        type: 'fixed',
        title: 'A failed card check no longer looks like success',
        description:
          "If a bank security check fails mid-payment, the customer now sees that the payment didn't complete — with a button to try again — instead of a thank-you page.",
      },
      {
        type: 'new',
        title: 'Preview the exact invoice the customer received',
        description:
          'On the billing page, sent invoices now have a "Preview invoice" button — it shows the real invoice document, rendered by the same system that makes the PDF.',
      },
      {
        type: 'improved',
        title: 'Proposal emails now match the rest',
        description:
          'Proposal emails use the same polished layout as invoice and receipt emails, so everything a customer gets from you looks like one company. The proposal email editor preview now shows exactly what sends.',
      },
    ],
  },
  {
    id: '2026-08-14-service-speed-and-action-item-comments',
    date: '2026-08-14',
    title: 'Plan speeds on services, and action item notes land on the ticket',
    tagline:
      'See what a customer’s plan actually runs at without opening the plan list — and stop losing follow-up notes on a separate screen.',
    items: [
      {
        type: 'new',
        title: 'Speed shows next to the plan on every service',
        description:
          'A service used to say "NBN Home Fast" and nothing else. It now shows the speed beside it — 100/20 Mbps — on the Services page and on the customer’s Services tab. Open the service and you also get the plan’s data allowance and tier. If the plan has no speed set, the speed the carrier actually provisioned is shown instead, marked as such.',
      },
      {
        type: 'improved',
        title: 'Speeds show while you are picking a plan',
        description:
          'The plan dropdowns on Add Service and Edit Service now read "NBN Home Fast · 100/20 Mbps — $99/mo" so you can pick the right tier without checking the plan list first.',
      },
      {
        type: 'new',
        title: 'Action item notes copy onto the deal, fault or service request',
        description:
          'Anything typed into an action item’s note box is now also added to the ticket it came from, so the customer’s story stays in one place. Raising and clearing the action item are recorded there too. Notes on the ticket are badged "via Action item" so you can see where they came from.',
      },
    ],
  },
  {
    id: '2026-08-14-billing-numbers-agree',
    date: '2026-08-14',
    title: 'Billing numbers now agree everywhere',
    tagline:
      'The billing page, its reports, and the invoice now show the same dollars for the same job.',
    items: [
      {
        type: 'fixed',
        title: 'Billing page totals match the cards and the invoice',
        description:
          'The To Bill / Awaiting Payment / Paid headline totals are now computed from the same line items the invoice actually charges. Awaiting Payment no longer counts money a customer already paid as a deposit, and Paid (30d) really is the last 30 days for faults and service requests too.',
      },
      {
        type: 'fixed',
        title: 'Discounts show up in billing previews',
        description:
          'A quote discount now appears as its own line on the billing card and deposit preview, so the preview total is the discounted amount the customer signed — not the pre-discount price.',
      },
      {
        type: 'fixed',
        title: 'Reports agree with their own CSV',
        description:
          'Billing report headline figures now include deposit-paid and flagged deals the detail rows always showed, paid figures subtract refunds, and activity near the start of a month lands in the right month.',
      },
      {
        type: 'fixed',
        title: 'GST-free jobs stay GST-free',
        description:
          'Updating a billing amount through the Theo connector no longer quietly adds GST back onto a job that was captured GST-free. Invoice due dates and dates on PDFs also stop drifting a day early in the morning.',
      },
    ],
  },
  {
    id: '2026-08-14-pm-meeting-after-scheduling',
    date: '2026-08-14',
    title: 'PM meetings now happen after scheduling',
    tagline:
      'Schedule and provision the deal first — then raise the PM meeting for the walkthrough.',
    items: [
      {
        type: 'improved',
        title: 'The PM meeting question is gone from the Won stage',
        description:
          'Winning a deal now goes straight to the Schedule & Provision choices. The initial project meeting happens outside Theo, so nothing to tick there anymore.',
      },
      {
        type: 'new',
        title: 'Raise a meeting from the deal or the schedule',
        description:
          'Once a deal is scheduled and provisioned, raise a meeting from the deal’s Actions menu or straight from the job’s panel on the schedule — pick what kind: project management meeting, toolbox talk, team meeting, or your own label. Deals flagged as needing a PM meeting will also prompt you on the deal page.',
      },
      {
        type: 'improved',
        title: 'A pending PM meeting no longer holds up scheduling',
        description:
          'Scheduling and provisioning carry on as normal while the meeting sits in the queue. Completing the meeting still puts the checklists and equipment on the install jobs.',
      },
    ],
  },
  {
    id: '2026-08-14-confirm-customer-number',
    date: '2026-08-14',
    title: 'Confirming a job stops saying "no number on file"',
    tagline:
      'It now looks at the number on the customer record, not just the one saved against the job.',
    items: [
      {
        type: 'fixed',
        title: 'Confirm with customer now finds the number on the customer record',
        description:
          'Some customers showed no contact details when you went to confirm a job — most often on jobs raised from a fault — even though their number was right there on their customer page. The confirm window was only looking at the number saved onto the job itself. It now checks the customer record too, so the number is there ready to go.',
      },
      {
        type: 'improved',
        title: 'A second customer number is offered as well',
        description:
          'If the customer has a second number on file, it now shows up in the "Send to" list alongside the first, with a landline warning if a text will not reach it.',
      },
    ],
  },
  {
    id: '2026-08-14-til-leave-type',
    date: '2026-08-14',
    title: 'Book time in lieu straight off the schedule',
    tagline:
      'TIL booked as annual leave never came out of the jar — the tech got the day and kept the banked hours.',
    items: [
      {
        type: 'new',
        title: 'TIL is now a leave type on the schedule',
        description:
          'Add an event, pick Leave, then TIL. It logs an approved time-in-lieu entry against that tech, so the hours actually come out of their banked-hours jar. Booking it as annual leave never did.',
      },
      {
        type: 'new',
        title: 'You can see their jar before you commit',
        description:
          'Pick the tech and the form shows what they have banked, what the block will cost, and what they are left with. If it costs more than they have banked, it warns you first — it still saves, they just end up owing the difference back.',
      },
      {
        type: 'improved',
        title: 'Deleting a TIL block gives the hours back',
        description:
          'Delete a time-in-lieu day off the schedule and the banked hours return to that tech. Switching a block from TIL to annual leave does the same.',
      },
      {
        type: 'improved',
        title: 'Banked hours update the moment you save',
        description:
          'The TIL figure on a tech\'s column used to take up to 15 minutes to catch up, which looked like the booking had not worked. Booking, approving, denying or cancelling time in lieu now moves it straight away.',
      },
      {
        type: 'fixed',
        title: 'Leave blocks open as the type they actually are',
        description:
          'Leave that came from a request used to open as "Annual" no matter what it really was — which is how a time-in-lieu day got quietly re-saved as annual leave. It now opens as the right type.',
      },
    ],
  },
  {
    id: '2026-08-14-multi-tech-jobs',
    date: '2026-08-14',
    title: 'Putting 3 or 4 techs on a job actually sticks now',
    tagline:
      'Techs you added could quietly fall off, and a tech marked away had no column to show up in at all.',
    items: [
      {
        type: 'fixed',
        title: 'Adding a 2nd, 3rd and 4th tech no longer drops the earlier ones',
        description:
          'Each tech you picked was worked out from the job as it looked when you first right-clicked, so a quick second or third pick could overwrite the one before it. Every add and remove is now written against the live job.',
      },
      {
        type: 'improved',
        title: 'The tech picker stays open',
        description:
          'Right-click a job, choose Add Tech, and keep picking — the list closes when you are done, not after every name. It shows how many are on the job and greys out anyone already there.',
      },
      {
        type: 'fixed',
        title: 'Dragging a job no longer wipes the rest of the crew',
        description:
          'Moving a shared job onto another tech\'s column used to leave just that one tech on it. Now only the tech you dragged moves across — everyone else stays on the job at the time they already had.',
      },
      {
        type: 'fixed',
        title: 'A tech on leave or a day off still gets a column',
        description:
          'If someone is marked away, their jobs used to render nowhere at all — the job existed and nobody could see it. They now get a normal column with an amber "on leave" badge so the clash is obvious.',
      },
      {
        type: 'new',
        title: 'Techs can see who else is on the job',
        description:
          'A shared job now shows the full crew on the tech\'s job card, with each person\'s own start time if they have one, and a call button for the others.',
      },
    ],
  },
  {
    id: '2026-08-14-share-cable-type',
    date: '2026-08-14',
    title: 'The shared link now says what cable to pull',
    tagline:
      'It was blank on every row — on the list and on the plan. The single most important thing on the sheet.',
    items: [
      {
        type: 'fixed',
        title: 'Cable type is on every row now',
        description:
          'The shared link was showing no cable at all — so a sparky could see where a run goes but not what to run. Names you type yourself ("3x Cat6", "32mm conduit") and cables picked from the catalogue both show now, on the list and when you tap an item on the plan.',
      },
      {
        type: 'improved',
        title: 'A run with no cable picked says so',
        description:
          'It used to just leave the line out, which looks the same as having no cable at all. It now says "Cable not picked yet" in amber, so it is obvious the office still has to fill it in.',
      },
    ],
  },
  {
    id: '2026-08-14-share-clusters-and-filters',
    date: '2026-08-14',
    title: 'The shared plan works on a big job now',
    tagline:
      'Seventy-nine items used to come out as a wall of circles with the drawing invisible underneath. They group up, the names thin out, and you can switch trades off.',
    items: [
      {
        type: 'new',
        title: 'Items that sit on top of each other group into one',
        description:
          'On a big job the pins covered the whole drawing. Ones too close together now show as a single circle with a count, like any map app. Tap it and it zooms in and splits apart. Tap again and you are on the item.',
      },
      {
        type: 'new',
        title: 'Turn trades on and off',
        description:
          'Chips along the top — Networking, Security, Smart Home, AV — each with a count. Tap one to hide it. Cuts the plan and the list together, so a sparky doing the speakers can hide everything else. "Show all" brings it back.',
      },
      {
        type: 'fixed',
        title: 'Names no longer print on top of each other',
        description:
          'Every item name was drawn at once, so on a busy plan they overlapped into a solid block of text. Now only the ones with room around them are shown, and more appear as you zoom in.',
      },
      {
        type: 'fixed',
        title: 'Tapping picks the item you actually tapped',
        description:
          'With items close together, a tap used to open whichever one the computer happened to draw last — so you tapped a camera and got a speaker across the room. It now picks the closest one to your thumb.',
      },
      {
        type: 'fixed',
        title: 'Zooming near an edge threw the plan off the screen',
        description:
          'Tapping to zoom, double-tapping, or scrolling to zoom moved the drawing about twice as far as it should, so it shot off the corner of the screen. Fixed.',
      },
    ],
  },
  {
    id: '2026-08-14-share-icons-and-zoom',
    date: '2026-08-14',
    title: 'Item icons on the shared link, and zoom that actually works',
    tagline:
      'The sparky now sees the same icons you drew, and can pinch the plan around like any map on their phone.',
    items: [
      {
        type: 'new',
        title: 'The icons from your plan are on the link',
        description:
          'Every pin on the shared plan now shows the actual item — access point, camera, speaker, TV, keypad — instead of a plain coloured dot, and the same icon appears next to each row on the list. Symbols you made yourself come through too.',
      },
      {
        type: 'fixed',
        title: 'Pinch to zoom now works',
        description:
          'Two fingers on the plan did nothing. It now pinches, and it zooms where your fingers are instead of always pulling to the middle of the screen. Double-tap zooms in on a spot, double-tap again goes back to the whole plan.',
      },
      {
        type: 'improved',
        title: 'Zooming in no longer buries the plan in pins',
        description:
          'The pins used to grow with the drawing, so zooming in to separate two items just made a bigger pile. They now stay the same size on screen, so zooming in spreads them out and you can read the drawing underneath.',
      },
      {
        type: 'improved',
        title: 'You can see how far zoomed in you are',
        description:
          'A small "2.4× · fit" button appears once you have zoomed. Tap it to snap back to the whole plan.',
      },
    ],
  },
  {
    id: '2026-08-14-share-link-lockdown',
    date: '2026-08-14',
    title: 'The sparky link is locked down, readable, and branded',
    tagline:
      'Otis was appearing on the shared cable link. He is gone from it, the buttons can be read again, undo works from the plan, and your logo is on it.',
    items: [
      {
        type: 'fixed',
        title: 'Otis no longer appears on shared links',
        description:
          'The floating Otis button was showing up on the cable link you send to electricians. It should never have been there — Otis can see the company’s whole history and that link goes to people outside the business. He is now switched off everywhere except the screens you sign in to, and any new public link is locked down automatically rather than having to be remembered.',
      },
      {
        type: 'fixed',
        title: 'Black buttons with black writing',
        description:
          'On the shared link the main tick-off button had black writing on a black background, so you could not read what it said. Fixed. The same problem on the "Try again" button on the proposal viewer and the invoice payment page is fixed too.',
      },
      {
        type: 'fixed',
        title: 'Undo now works from the plan',
        description:
          'Tapping an item on the plan and ticking a cable off gave you no way to change it back — the undo button was only on the list. It is on both now.',
      },
      {
        type: 'improved',
        title: 'Your logo on the shared link',
        description:
          'The link now opens with your logo and company colour, carries a title block with your logo in the corner of the plan, and shows progress as a filling ring instead of two small numbers. Useful when a subbie is holding links from three different companies.',
      },
    ],
  },
  {
    id: '2026-08-14-pdf-label-overlap',
    date: '2026-08-14',
    title: 'Items no longer hide behind each other on the printed plan',
    tagline:
      'A keypad next to a touchscreen was disappearing under the other one’s name tag. Names now step aside and point back with a line.',
    items: [
      {
        type: 'fixed',
        title: 'Two items side by side both show up now',
        description:
          'Names were drawn one at a time, right next to their own item — so on a wall with a keypad and a touchscreen, the first name tag sat on top of the second item and it vanished off the pack. Names are now placed after every item is drawn, and any that would cover something moves to the nearest free spot with a thin line pointing back to where it belongs.',
      },
      {
        type: 'improved',
        title: 'Plans that were fine still look the same',
        description:
          'An item with room around it keeps its name in the usual place with no line — nothing changes on an uncrowded drawing.',
      },
    ],
  },
  {
    id: '2026-08-14-cable-names-and-grouping',
    date: '2026-08-14',
    title: 'Name your own cables, and see a TV’s cables together',
    tagline:
      'Type whatever the sparky calls it instead of hunting the parts list — and every cable running to one item now sits in one block.',
    items: [
      {
        type: 'new',
        title: 'Type your own cable name',
        description:
          'The Cable column is now a free text box. Type "2C 14AWG Speaker" or "Cat6 white" — whatever the sparky needs to read. Picking one of your stocked cables from the list still works and still prices the run.',
      },
      {
        type: 'new',
        title: 'Names you have used come back as suggestions',
        description:
          'Name a cable once on run 3 and it is offered on run 40. It reads the names off the job itself, so there is no list to maintain.',
      },
      {
        type: 'improved',
        title: 'A typed name never changes the price',
        description:
          'Your name is what gets printed; the stocked item is what gets costed, and they are separate. A row named by hand with nothing stocked behind it shows a small $0 so you can see it carries no cost.',
      },
      {
        type: 'fixed',
        title: 'A TV’s cables now sit together',
        description:
          'The schedule grouped by trade, so the Cat6, the HDMI and the coax for TV 1 landed in three different sections. It now groups by item — TV 1 gets one block with its three cables in it. Single-cable items stay as plain rows so the sheet does not fill up with headings.',
      },
      {
        type: 'improved',
        title: 'Switch back to trade grouping any time',
        description:
          'By item / By trade sits next to the Columns button. Trade grouping is still what you want for a sub-contractor’s copy. It remembers your choice per project.',
      },
    ],
  },
  {
    id: '2026-08-14-rooms-fixed',
    date: '2026-08-14',
    title: 'Rooms actually work now — and there is a Rooms list',
    tagline:
      'Drawing a room was giving you the wrong panel, so the name went in the wrong box and nothing picked it up. Fixed, with a one-click rescue for the ones you already drew.',
    items: [
      {
        type: 'fixed',
        title: 'Drawing a room now asks you which room it is',
        description:
          'When you finished drawing an area, the panel that opened was the normal item panel with a "Label" box — not the room picker. So the name you typed labelled the shape instead of naming a room, and nothing inside it reported anything. The right panel opens now, straight after you draw.',
      },
      {
        type: 'new',
        title: 'One click to rescue the areas you already drew',
        description:
          'Open Rooms and any area you drew but never named is listed at the top, with the name you typed on it already filled in. Hit "Make it a room" and everything inside it starts reporting straight away — no redrawing.',
      },
      {
        type: 'new',
        title: 'A Rooms list',
        description:
          'The cog on the Rooms panel on the plan opens the whole list: rename, add, delete, and see how many items are in each. It tells you how many items report a room before you delete it.',
      },
      {
        type: 'improved',
        title: 'Rooms are much quieter on the drawing',
        description:
          'Room shading and outlines now sit at about 20% so they read as background. The room name stays full strength, because that is the part you actually need.',
      },
      {
        type: 'improved',
        title: 'Every room gets its own colour',
        description:
          'Colours are assigned automatically and no longer start repeating after twelve rooms — a twenty-room house gets twenty distinguishable colours.',
      },
    ],
  },
  {
    id: '2026-08-14-cables-and-site-map',
    date: '2026-08-14',
    title: 'Pick the cable, run four to a TV, and give the sparky the plan',
    tagline:
      'The Cable column is now a dropdown, a TV gets its three cables instead of one, and the live link has the drawing on it.',
    items: [
      {
        type: 'fixed',
        title: 'You can actually set the cable now',
        description:
          'The Cable column was read-only text — the one thing the schedule is for could not be entered on the schedule. It is a dropdown of your cable stock, and the run re-prices itself when you change it.',
      },
      {
        type: 'new',
        title: 'Two to four cables to one item',
        description:
          'A TV point is never one cable. Add plan items to the schedule and a TV now gets three rows — data ×2, HDMI, coax — each with its own row on the sparky pack. Same for projectors, keypads and data plates.',
      },
      {
        type: 'new',
        title: '"Another cable to this item"',
        description:
          'A button on every row adds a second (or fifth) cable to the same device, keeping the same from and to. For anything the presets do not predict.',
      },
      {
        type: 'new',
        title: 'The sparky gets the plan on their phone',
        description:
          'The live link now has a Plan tab: pinch to zoom, drag to move, tap any item to see its cables and mark them off. It stays smooth because the drawing is rendered once and scaled, not re-rendered every pinch.',
      },
      {
        type: 'new',
        title: 'They can photograph the cable as proof',
        description:
          'A camera button on every run. The photo shrinks on their phone before it sends, so it works on one bar, and it shows up against that run for you.',
      },
      {
        type: 'new',
        title: 'Your plan shows what has been pulled',
        description:
          'Every item on the drawing wears a dot: amber when some of its cables are in, a green tick when they all are. That is the sparky ticking off on their phone, showing up on your plan.',
      },
      {
        type: 'improved',
        title: 'Links now last 90 days',
        description: 'Up from 60, so one link covers rough-in and fit-off.',
      },
    ],
  },
  {
    id: '2026-08-13-live-sparky-link',
    date: '2026-08-13',
    title: 'Send the sparky a live link instead of a PDF',
    tagline:
      'One link, the whole crew. They see the schedule as it stands and tick runs off as they pull them — and you can switch it off any time.',
    items: [
      {
        type: 'new',
        title: 'Export → Live link instead',
        description:
          'Creates a link you can text to the electrician. They open it on their phone — no login, no app — and see the same schedule you are looking at, always current.',
      },
      {
        type: 'new',
        title: 'They tick runs off as they go',
        description:
          'One tap moves a run to Pulled, then Terminated, then Tested. It lands on your schedule straight away, so you can see how the rough-in is going without ringing anyone.',
      },
      {
        type: 'new',
        title: 'The whole crew can share one link',
        description:
          'Everyone opens the same link and types their name once on their own phone. Every tick is stamped with who did it, so you can see whose work is whose and they can see each other’s progress.',
      },
      {
        type: 'new',
        title: 'You can switch it off',
        description:
          'Links last 60 days and you can kill any of them instantly from the same menu. Anything already ticked off stays ticked. That is the bit a PDF can never do.',
      },
      {
        type: 'improved',
        title: 'No prices on it, ever',
        description:
          'Costs and sell prices never leave our server — they are not hidden on the page, they are simply not sent. And the link can only mark runs pulled, terminated or tested; it can never certify a run or take one off the job.',
      },
    ],
  },
  {
    id: '2026-08-13-schedule-columns',
    date: '2026-08-13',
    title: 'Turn off the schedule columns you don’t use',
    tagline:
      'No conduit on this build? Switch the column off. What’s left gets wider, and the sparky pack prints exactly what you’re looking at.',
    items: [
      {
        type: 'new',
        title: 'A Columns button on the schedule',
        description:
          'Tick the columns you want and untick the ones you don’t. Conduit, hole, termination, cost, status, who’s cabling, who’s fitting off — all optional. It remembers your choice for each project.',
      },
      {
        type: 'improved',
        title: 'Fewer columns means wider names',
        description:
          'The device name and the "what for" column take up whatever space you free. Switch four columns off and the names get noticeably easier to read.',
      },
      {
        type: 'improved',
        title: 'The sparky pack prints what you see',
        description:
          'Export → Sparky pack now uses exactly the columns you have on screen, and lists them in the menu so you can check before you generate it. Costs are never included, whatever you have ticked.',
      },
      {
        type: 'new',
        title: 'Pick the From and To off the plan',
        description:
          'The From and To cells now offer every device you have drawn — start typing and pick the rack. Picking one links the run to that item on the plan, so clicking the row highlights it on the drawing. You can still type anything for the things that were never drawn.',
      },
    ],
  },
  {
    id: '2026-08-13-plan-items-to-schedule',
    date: '2026-08-13',
    title: 'Put what you drew straight onto the cable schedule',
    tagline:
      'Forty things on the plan and an empty schedule was never right. One button now turns every item you drew into a row.',
    items: [
      {
        type: 'new',
        title: 'The schedule tells you what is missing',
        description:
          'A strip across the top of the cable schedule says how many items on the plan have no cable yet. Click it and every one of them becomes a row — named after the item, with its cable type and the room it is drawn in already filled in.',
      },
      {
        type: 'new',
        title: 'Pick where it all runs back to',
        description:
          'Before it creates the rows it asks what everything terminates into. It suggests the rack you drew, or an area you marked as the head end. You can also say "nothing yet" and set the source later.',
      },
      {
        type: 'improved',
        title: 'Safe to press twice',
        description:
          'Anything already on the schedule is left alone, so you can drop a few more items on the plan next week and click it again without ending up with duplicates.',
      },
      {
        type: 'improved',
        title: 'It tells you what it will cost you',
        description:
          'Adding rows changes the project’s planned cost, so the window says so before you commit, and shows how many rows have no cable type yet. Lengths start at zero — it never guesses one.',
      },
    ],
  },
  {
    id: '2026-08-13-location-zones',
    date: '2026-08-13',
    title: 'Draw a room once, stop labelling every item',
    tagline:
      'Draw an area on the plan, name it, and everything inside picks the name up on its own — on the plan, on the cable schedule and in the printed pack.',
    items: [
      {
        type: 'new',
        title: 'Draw an area and name it',
        description:
          'Use the Region tool to draw around a room, then pick a name from "This area is" — Master Bedroom, Kitchen, whatever you like, and you can add a new one right there. The shape takes the room colour and wears the name on the drawing, so you can read the plan without clicking anything.',
      },
      {
        type: 'new',
        title: 'Everything inside picks the room up automatically',
        description:
          'Any keypad, speaker, sensor, AP or camera you drop inside the area reports that room. No more calling something "Ceiling speaker bed 2" just so you can tell where it is. Move an item to another room and its location follows straight away.',
      },
      {
        type: 'new',
        title: 'Override any single item',
        description:
          'Click an item and the Location box lets you set it by hand — Roof Space, another room, or nothing at all. A hand-set location always beats the drawing, and the panel shows AUTO or SET so you can tell which is which at a glance.',
      },
      {
        type: 'new',
        title: 'The sparky sees it on the schedule',
        description:
          'The Room column on the cable schedule fills in from the drawing, so a run reads "Master Bedroom" instead of a dash. Anything you typed yourself stays exactly as you typed it. The sparky pack, the full schedule and the CSV all carry it.',
      },
      {
        type: 'new',
        title: 'A room count on the plan',
        description:
          'The bottom-left of the plan lists each room with how many items are in it, plus how many are not in any room yet — so you can see when the drawing is actually finished, and it warns you if two areas overlap.',
      },
    ],
  },
  {
    id: '2026-08-13-provision-flag',
    date: '2026-08-13',
    title: 'Mark something as a provision with a tick',
    tagline:
      'No more typing *provision* into the name. Tick the box and it shows up on the plan, on the cable schedule and in the PDF.',
    items: [
      {
        type: 'new',
        title: 'A Provision tick box on every plan item',
        description:
          'Click an item on the plan and you will see "Run the cable, gear not confirmed". Tick it and the item gets a dashed ring and a PROV tag on the drawing, so anyone looking at the plan knows to pull cable there without assuming the gear is coming.',
      },
      {
        type: 'new',
        title: 'It shows on the cable schedule too',
        description:
          'Any run that ends at a provision gets an orange PROV tag next to its run number, so the sparky reading the schedule sees it on the row they are actually looking at.',
      },
      {
        type: 'new',
        title: 'And in the exported PDF',
        description:
          'Provisions print with a dashed ring and (PROV) after the name, and the front page tells you how many are on the plan.',
      },
      {
        type: 'improved',
        title: 'It offers to tidy up the ones you already typed',
        description:
          'If an item is still called something like "TV point *provision*", the panel offers to tick the box and clean the name up for you in one click.',
      },
      {
        type: 'improved',
        title: 'A count in the bottom bar',
        description:
          'The cost bar shows PROV and how many there are, with what they are worth. Your totals do not change — provisions still count exactly as they did — it just tells you how much of the number is not locked in yet.',
      },
    ],
  },
  {
    id: '2026-08-13-custom-plan-symbols',
    date: '2026-08-13',
    title: 'Make your own plan symbols',
    tagline:
      'Name it, colour it, pick an icon from about 1,300. It shows up in the symbol list for every project.',
    items: [
      {
        type: 'new',
        title: 'New symbol button in the symbol list',
        description:
          'Open the plan editor, open the Symbols panel, and hit New symbol. Give it a name, pick a category, pick a colour, and search a library of around 1,300 icons for one that suits. Save it and it is ready to drop straight onto the plan.',
      },
      {
        type: 'new',
        title: 'Your own colours',
        description:
          'A symbol you make keeps the colour you gave it instead of taking its category colour. It looks the same on the plan, in the cable workspace, and in the exported PDF.',
      },
      {
        type: 'new',
        title: 'Shared across every project',
        description:
          'Symbols you make are saved for the whole company, not just the plan you were on. Make an Intercom panel once and it is there on the next job.',
      },
      {
        type: 'improved',
        title: 'Edit or delete the ones you made',
        description:
          'Hover one of your symbols in the list and hit the pencil to rename it, recolour it, or change its icon. Renaming is safe — anything already placed on a plan follows the change. If you delete one that is in use it tells you how many are out there first.',
      },
    ],
  },
  {
    id: '2026-08-13-plan-zoom-flicker-really-fixed',
    date: '2026-08-13',
    title: 'Zooming a plan is smooth now — properly',
    items: [
      {
        type: 'fixed',
        title: 'No more white flashing when you zoom in and out',
        description:
          'Every click of the mouse wheel was making the plan redraw itself from scratch, and zooming back out redrew every size again on the way down — so it flashed white and kept snapping in and out. It now redraws about twice for a whole zoom in and back, instead of eight or more times. Zoom in to read something and back out and it does not redraw at all on the way out.',
      },
    ],
  },
  {
    id: '2026-08-13-cable-plan-split-and-fullscreen',
    date: '2026-08-13',
    title: 'Cable + Plan: full screen, and any split you like',
    tagline:
      'Give the drawing 70% and the schedule 30%, or whatever suits — and put the whole lot on the screen with nothing else on it.',
    items: [
      {
        type: 'new',
        title: 'Full screen',
        description:
          'A Full screen button in the top right of Cable + Plan. It takes the plan AND the schedule with it, so you keep working the same way, just bigger. Press Esc to come back. Works whether you are editing the plan or just looking at it.',
      },
      {
        type: 'improved',
        title: 'Drag the divider to any split you want',
        description:
          'Grab the line between the plan and the schedule and drag. It now goes much further either way, snaps to the tidy ratios as you pass them, and shows you the numbers while you drag. Double-click it to go back to half and half. Hold Alt if you want to stop it snapping.',
      },
      {
        type: 'new',
        title: 'Pick a split in one click',
        description:
          'Next to the Plan / Split / Schedule buttons there is now a 70 / 30 style picker. Choose the split you want instead of dragging for it.',
      },
      {
        type: 'improved',
        title: 'Your split is remembered',
        description:
          'Whatever you set it to is what you get next time you open Cable + Plan.',
      },
      {
        type: 'fixed',
        title: 'The plan resizes with the pane',
        description:
          'Making the plan side bigger or smaller used to leave the drawing at its old size, so it looked wrong until you reloaded. It now re-fits as you drag, and a tall portrait plan fits properly instead of having its bottom cut off.',
      },
    ],
  },
  {
    id: '2026-08-13-plan-editor-smooth-zoom',
    date: '2026-08-13',
    title: 'The plan editor stopped flickering',
    tagline:
      'Zoom and pan a floor plan without the drawing blinking at you — and hold the middle mouse button to grab it.',
    items: [
      {
        type: 'fixed',
        title: 'No more flicker when you zoom a floor plan',
        description:
          'Zooming in the plan editor made the drawing redraw itself over and over, so it flashed and crawled — worst on a big A1 sheet. It now redraws once, after you stop moving, and stays smooth the whole way in and out.',
      },
      {
        type: 'new',
        title: 'Hold the middle mouse button to grab the plan',
        description:
          'Press and hold the middle mouse button (the scroll wheel) anywhere on the plan and drag to move around, whatever tool you have selected. Let go and you are back to what you were doing. Space-drag and the Pan tool still work the same.',
      },
      {
        type: 'fixed',
        title: 'The plan no longer goes blank at high zoom',
        description:
          'Zooming right in on a large sheet could leave the drawing blank. The plan stays visible all the way to the maximum zoom now.',
      },
    ],
  },
  {
    id: '2026-08-13-completion-notes-reach-the-deal',
    date: '2026-08-13',
    title: 'Tech completion notes reach the deal again',
    tagline:
      'If a job had gear on it, the note the tech wrote at completion was being thrown away. Fixed.',
    items: [
      {
        type: 'fixed',
        title: 'Your completion note now saves on jobs that have gear listed',
        description:
          'When a tech finished a job that had parts on it, the whole completion form was being discarded the moment it saved — the note, the parts they ticked off, any site WiFi details, and the tagged field log. The job still closed and nothing looked wrong, so the office opened the deal and found no notes from the visit. Jobs with no parts listed were unaffected, which is why pre-sites kept working. Everything now saves as entered.',
      },
      {
        type: 'fixed',
        title: 'Return visits are actually created again',
        description:
          'When a tech flagged that a job needs a return visit, the follow-up job was often never created at all — and the deal never heard about it either. It failed silently on any job that had no field log entries, which is most of them. Fixed.',
      },
      {
        type: 'improved',
        title: 'You get told if your completion details do not save',
        description:
          'If saving ever fails again, the tech app now says so on the spot instead of closing the job quietly, so you can re-enter it or tell the office before you leave site.',
      },
    ],
  },
  {
    id: '2026-08-11-quote-requested-status',
    date: '2026-08-11',
    title: 'New "Quote Requested" provisioning status',
    items: [
      {
        type: 'new',
        title: 'Mark gear as "Quote Requested"',
        description:
          'The status dropdown on Provisioning now has a Quote Requested option, so you can show you have asked a supplier for a price before the item is actually ordered. It sits between Not Ordered and Ordered, and does not count as ready for scheduling.',
      },
    ],
  },
  {
    id: '2026-08-11-line-items-reach-provisioning',
    date: '2026-08-11',
    title: 'Items added after a deal is won now reach Provisioning',
    tagline:
      'Add gear to a won deal and it shows up on the provisioning board — including the items that went missing before today.',
    items: [
      {
        type: 'fixed',
        title: 'Gear added through Manage Line Items now lands in Provisioning',
        description:
          'On deals that never went through Quote Review — anything moved to Won by hand, signed in Better Proposals, or brought over from Pipedrive — items added from the catalog, Add Service, or Manual Item were saved on the deal but never made it onto the provisioning board. No warning, nothing to notice. They now go straight through, and so do quantity and price changes.',
      },
      {
        type: 'fixed',
        title: 'Missing items are filled in automatically',
        description:
          'Deals that already lost items this way get them back. Opening the Provisioning page, or Manage Line Items on the deal, now checks the deal against the board and adds anything that never arrived. Existing items are matched, not duplicated.',
      },
    ],
  },
  {
    id: '2026-08-11-deal-workspace-declutter',
    date: '2026-08-11',
    title: 'Deals stopped shouting at you',
    tagline:
      'Fewer cards, no more empty boxes, and the thing you actually have to do sits at the top.',
    items: [
      {
        type: 'improved',
        title: 'The decision comes first',
        description:
          'On a won deal, "choose how to proceed" used to sit below the site report, the proposal and the visit list. It now sits directly under the customer bar, so you pick a route the moment you open the deal.',
      },
      {
        type: 'improved',
        title: '"Automatic step" moved up next to the stage bar',
        description:
          'The line telling you the deal moves on by itself now sits right under the stage dots at the top, where it explains them — instead of floating halfway down the page between unrelated cards.',
      },
      {
        type: 'fixed',
        title: 'No more "Build Quote" on a deal that is already booked in',
        description:
          'Deals quoted through Better Proposals were showing a "No quote created yet" box with a Build Quote button, even at Scheduling with the gear at the office. That box no longer appears when there is nothing to show.',
      },
      {
        type: 'improved',
        title: 'Open proposal is a quiet button once the deal is sold',
        description:
          'From Provisioning onward the proposal is something you look up, not the next thing to do — so it no longer sits in the corner as the big highlighted button.',
      },
      {
        type: 'improved',
        title: 'The same visit stopped showing up twice',
        description:
          'Work done on site and Site visits were both printing the same date, tech and time on site. Once every visit is finished, Site visits folds into a single line you can tap open when you need the scheduler link or to fix a timer.',
      },
      {
        type: 'fixed',
        title: 'No more "send it to the customer" on a deal they already signed',
        description:
          'Deals won through Better Proposals were still showing a big Open proposal button telling you to send one. That card now reads as the accepted quote, with the builder as a small link.',
      },
      {
        type: 'improved',
        title: 'Quieter sidebar',
        description:
          'A finished quote review is one green line instead of a stat block, the Better Proposals card folds to a single row once the deal is won (it was still saying "Sent to Customer" on booked-in jobs), and the empty "No Project Linked" box — which never had a button on it — is gone.',
      },
    ],
  },
  {
    id: '2026-08-11-note-replies-and-tagging',
    date: '2026-08-11',
    title: 'Reply to notes, and tag people mid-sentence',
    tagline:
      'Notes are conversations now — reply to any note, and @ works wherever you are in the sentence.',
    items: [
      {
        type: 'new',
        title: 'Reply to any note',
        description:
          'Every note on a deal, fault, service request, job, customer, the scheduler and billing now has a Reply button. Replies sit under the note they answer instead of scattering down the timeline, and everyone already in the thread gets a notification — no need to @ them again.',
      },
      {
        type: 'fixed',
        title: '@ now works mid-sentence',
        description:
          'Tagging someone used to only work if the @ was the first thing you typed or came straight after a space. Now it works after a full stop, a comma, a bracket, a dash or a line break — anywhere the @ starts a word.',
      },
      {
        type: 'improved',
        title: 'Two people with the same first name get tagged correctly',
        description:
          'Picking someone from the list now writes a handle that points at them specifically, so a tag meant for one Sam no longer lands in the other Sam’s notifications. Email addresses typed into a note also stop firing off accidental tags.',
      },
      {
        type: 'new',
        title: 'Customer notes can tag people too',
        description:
          'The note box on a customer record now has the same @ picker as everywhere else, and the people you tag actually get notified.',
      },
    ],
  },
  {
    id: '2026-08-11-starlink-kit-speed-labels',
    date: '2026-08-11',
    title: 'Provisioning says which Starlink kit to order',
    tagline:
      'Every Starlink kit card now shows its speed plan and whether the service is self-managed.',
    items: [
      {
        type: 'new',
        title: 'Starlink kit cards show the speed plan',
        description:
          'A Starlink kit on the provisioning board now carries a label like STARLINK KIT - 200 or STARLINK S/M KIT - MAX, so you can order the right kit without opening the deal. The label shows in the deal card, in the drawer and in the Order by Supplier list.',
      },
      {
        type: 'new',
        title: 'Set the speed plan and self-managed on the card',
        description:
          'Open a Starlink kit and pick 100, 200 or MAX, and whether Techery holds the account or the customer runs it themselves. New deals fill it in automatically from the Starlink plan on the quote; anything it cannot work out shows an amber label until you pick one.',
      },
      {
        type: 'new',
        title: 'Self-managed is captured on the deal',
        description:
          'The Starlink section of a deal now has a "Self-Managed Service?" tick, so the kit label is right from the moment provisioning is generated.',
      },
    ],
  },
  {
    id: '2026-08-11-towns-back-on-addresses',
    date: '2026-08-11',
    title: 'Addresses show the town again',
    tagline: 'Street, suburb, state and postcode — everywhere an address appears.',
    items: [
      {
        type: 'fixed',
        title: 'The town is back on customer, deal, job and provisioning addresses',
        description:
          'Lots of screens were showing only the street number and street name — no town or city. Customers, deals, quotes, billing, the scheduler, the jobs list, the map, the office dashboard and the tech day view now all show the full address.',
      },
      {
        type: 'fixed',
        title: 'Navigate and drive-time now use the whole address',
        description:
          'Tapping Navigate on a job or a customer used to send you to whichever matching street name the maps app picked first. It now uses the full address, so you get the right town. Same for the ETA text drive-time estimate and the office map pins.',
      },
      {
        type: 'fixed',
        title: 'Follow-up and quick-raised jobs keep their town',
        description:
          'A return visit, or a job raised off a service request, used to be created without the suburb, state or postcode — so the tech saw a bare street number on the day list. Those jobs now carry the full address from the moment they are made.',
      },
      {
        type: 'improved',
        title: 'Provisioning shows where the gear is going',
        description:
          'The provisioning detail panel now shows the site address at the top, and the Starlink service address field opens pre-filled from the deal instead of blank.',
      },
    ],
  },
  {
    id: '2026-08-11-copyable-action-items',
    date: '2026-08-11',
    title: 'Action items you can actually copy',
    items: [
      {
        type: 'improved',
        title: 'Select and copy an action item',
        description:
          'The title, customer, address and notes on an action item are now normal selectable text — highlight and copy any of it. Hover a title for a one-click copy button, and use "Copy" on the card to grab the whole thing (title, customer, address, who raised it, assignees, tags and notes) ready to paste into an email or a message. The action item on a job in the schedule panel has a copy button too.',
      },
    ],
  },
  {
    id: '2026-08-10-presite-stops-at-quoting',
    date: '2026-08-10',
    title: 'Finished pre-sites stop at Quoting again',
    items: [
      {
        type: 'fixed',
        title: 'Pre-site deals no longer jump to Proposal Sent',
        description:
          'When a tech finished a pre-site, the deal sometimes skipped straight past Quoting into Proposal Sent — with no proposal actually sent to anyone. A finished pre-site now lands in Quoting and stays there until you send the proposal.',
      },
    ],
  },
  {
    id: '2026-08-10-presite-notes-and-full-address',
    date: '2026-08-10',
    title: 'Pre-sites lead with the office notes — and the full address is back',
    tagline:
      'Open a pre-site and the first thing you read is why you are going. Job addresses now show the street, not just the suburb.',
    items: [
      {
        type: 'improved',
        title: 'Pre-site jobs open on the office notes',
        description:
          'On a pre-site, what the office wrote when the job came in is now the first thing on the card, in full — no tapping, no "show more". Same on your own jobs and on a teammate\'s job in Team Schedule.',
      },
      {
        type: 'fixed',
        title: 'Full street address, not just the suburb',
        description:
          'Some jobs only showed the suburb — "BOWRAL" — and Navigate dropped you in the middle of town. Where the job has no street on it, we now fill it in from the customer\'s record, so the address you read and the address you navigate to are the real one.',
      },
    ],
  },
  {
    id: '2026-08-10-timesheet-leave-hub',
    date: '2026-08-10',
    title: 'Timesheet & Leave — one place for your time',
    tagline:
      'Leave requests now live with your timesheet and banked hours — and you can spend banked time a few hours at a time.',
    items: [
      {
        type: 'new',
        title: 'Take banked hours off — any amount, not just a full day',
        description:
          'Got 5 hours in your jar? Ask for 5 hours off. Pick how long with the + and − buttons or the slider, then choose leave early, start late, or your own times. The hours only come out of your jar once the office approves it.',
      },
      {
        type: 'improved',
        title: 'Two big buttons: banked time, or sick and annual leave',
        description:
          'The tech menu\'s "My Timesheet" is now "Timesheet & Leave" — your hours, your banked time and your requests on one page, with two large buttons up top so asking for time off is one tap.',
      },
      {
        type: 'new',
        title: 'It tells you if you\'re booked on a job then',
        description:
          'Picking a day or a time window shows any jobs you\'re already booked on, and flags the ones the customer has confirmed. It doesn\'t stop you asking — most jobs can be moved — you just both know before it\'s a surprise.',
      },
      {
        type: 'new',
        title: 'Pending time off shows on the schedule with a "?"',
        description:
          'The moment someone asks for time off it appears on the scheduler as a dashed block with a "?" badge. Office: click it to approve, deny with a reply, or ask a question — no need to open the leave page.',
      },
      {
        type: 'improved',
        title: 'You hear back on your requests',
        description:
          'Approvals, denials, and any comments from the office now ping your notification bell, and the reply shows under the request on your Timesheet & Leave page.',
      },
    ],
  },
  {
    id: '2026-08-10-project-photos-and-zoom',
    date: '2026-08-10',
    title: 'Project photos, towns on the card, and a plan that zooms properly',
    tagline:
      'Recognise a project by its site photo, see which town it is in, and zoom a floor plan without the drawing blinking at you.',
    items: [
      {
        type: 'new',
        title: 'An overview photo for each project',
        description:
          'Open a project and drop a site photo at the top of the Overview tab. It becomes the picture on that project\'s card in the list, so you can spot the job you want without reading every name.',
      },
      {
        type: 'fixed',
        title: 'The city / town shows up now',
        description:
          'Projects had a spot for the town but nothing ever filled it in. New projects ask for it (pre-filled from the customer), older ones work it out from the site address, and you can click it in the project header to correct it.',
      },
      {
        type: 'fixed',
        title: 'Zooming a floor plan no longer flickers',
        description:
          'The plan used to blink and lag on every scroll of the wheel. It now zooms smoothly and re-sharpens once you stop, on both the quick view and the full editor.',
      },
      {
        type: 'improved',
        title: 'Zoom goes further, and Cable + Plan opens wide',
        description:
          'You can now zoom out far enough to see a whole A1 sheet and in far enough to read a plate label. Cable + Plan also opens with the sidebar tucked away so the drawing and the schedule get the whole window — the "Wide" button in the project bar brings it back.',
      },
    ],
  },
  {
    id: '2026-08-10-cable-plan-workspace',
    date: '2026-08-10',
    title: 'Plans and the cable schedule are now one screen',
    tagline:
      'Drawing on the left, schedule on the right. Click anything on the plan and fill in where it runs from and to — without leaving the page.',
    items: [
      {
        type: 'new',
        title: 'Cable + Plan: one tab instead of two',
        description:
          'The old "Plans" and "Cable Schedule" tabs are now a single "Cable + Plan" section. You can show just the drawing, just the schedule, or both side by side. Old links still work — they bring you here.',
      },
      {
        type: 'new',
        title: 'Click a point on the plan, edit its run',
        description:
          'Clicking a symbol or a cable line on the drawing jumps to its row in the schedule and highlights it. Clicking a row lights it up on the drawing. No more hunting for which run is which.',
      },
      {
        type: 'new',
        title: 'The detail a sparky actually needs',
        description:
          'Every run now records from, to, room, how many cables, conduit size, hole size, how it terminates, and what it is for — typed straight into the table, no pop-ups. Runs are grouped by trade (Network, AV, Lighting, Power…) with a colour each.',
      },
      {
        type: 'new',
        title: 'Export a sparky pack: drawing + schedule in one PDF',
        description:
          'The Export button builds one PDF with the marked-up plan pages followed by the run list, with a tick box per run for site. Prices are never on it. There is also a full-detail PDF for the office and a CSV for spreadsheets.',
      },
      {
        type: 'new',
        title: 'Draw on the plan and fill in the schedule, side by side',
        description:
          'Hit "Edit plan" in the Cable + Plan tab and the full plan editor opens right there in the split — drop access points, draw cable runs, set the scale — with the schedule live next to it. Draw a run on the left, fill in where it goes on the right, without ever changing screens. Drag the bar between them to give the drawing more room.',
      },
      {
        type: 'new',
        title: 'Say who runs the cable and who fits it off',
        description:
          'Every run has two new columns: who pulls the cable, and who fits it off — us, the sparky, or someone else. Set them one at a time, or use the buttons above the table to set every run showing at once. They print on the sparky pack, so nobody turns up expecting someone else to have done it.',
      },
      {
        type: 'improved',
        title: 'Ordering counts every cable, not every row',
        description:
          'If a run is two or three cables, say so in the Qty column — the cable order, terminations and costing now all count each cable. Runs without a quantity are treated as one, exactly as before.',
      },
    ],
  },
  {
    id: '2026-08-10-completion-recap-editing',
    date: '2026-08-10',
    title: 'Fix the facts on the "Before you finish" screen',
    tagline:
      'The last screen before a job closes now lets you correct things on the spot instead of backing out.',
    items: [
      {
        type: 'new',
        title: 'Adjust your on-site time before finishing',
        description:
          'Tap the "On site" number to set how long you were really there — handy when the timer started late or never ran. The office sees both your number and what the timer recorded.',
      },
      {
        type: 'new',
        title: 'Fix parts without backing out',
        description:
          'Tap a part to flip it between installed and not installed, use − / + to fix quantities, and tick everything in one go if the gear list was never ticked off.',
      },
      {
        type: 'new',
        title: 'Add gear you used that was never on the list',
        description:
          'Tap "Add gear you used" to search the parts list and add what you actually used — or just type a name if it is not in there. Tap the same part again for another one. Added something by mistake? Tap it to take it off.',
      },
      {
        type: 'new',
        title: 'Read and edit your notes on the recap',
        description:
          'Your completion note is shown in full and can be edited right there, and your field log entries are readable so you know exactly what the office will get.',
      },
      {
        type: 'fixed',
        title: 'Recap now shows what you just submitted',
        description:
          'The "Before you finish" screen sometimes showed 0 parts, no note and gear "on the van" straight after you filled in the completion form. It now always reflects what you just entered.',
      },
    ],
  },
  {
    id: '2026-08-10-financial-visibility',
    date: '2026-08-10',
    title: 'Money is now hidden unless you turn it on',
    tagline:
      'Prices, deal values and invoice totals only show for directors and the people you switch on.',
    items: [
      {
        type: 'new',
        title: 'Dollar figures are hidden by default',
        description:
          'Everywhere a dollar amount used to show — deal values, quote and invoice totals, pipeline and revenue numbers — it now reads •••• unless your account is cleared to see money. Nothing else about the page changes.',
      },
      {
        type: 'new',
        title: 'Settings → Financials',
        description:
          'A new page listing everyone on the team with a switch each. Directors are always on and can’t be switched off. Only admins can change the switches.',
      },
      {
        type: 'improved',
        title: 'Money-only pages ask you to get access',
        description:
          'Billing, Starlink billing, service metrics and project financials show a short "financial figures hidden" message instead of a page full of blanks. The Billing link also disappears from the menu if you can’t see money.',
      },
      {
        type: 'improved',
        title: 'Customers still see their prices',
        description:
          'Proposals, payment links and plan approvals sent to customers are unaffected — they always show the real amounts.',
      },
    ],
  },
  {
    id: '2026-08-10-slack-settings',
    date: '2026-08-10',
    title: 'Choose what lands in Slack — and where',
    tagline:
      'Admins can now pick which updates go to Slack and which channel each one posts into.',
    items: [
      {
        type: 'new',
        title: 'Slack settings page',
        description:
          'Admin → Automations → Slack lists every update Theo can post — deals won, quoting, scheduling, customer contact, provisioning, billing, and job photos. Flip each one on or off and paste the Slack link for the channel it should go to.',
      },
      {
        type: 'new',
        title: 'See that it’s working at a glance',
        description:
          'The page shows whether Slack is connected, which updates are on, and when each one last posted — and it warns you if a post failed so you can fix the link.',
      },
      {
        type: 'new',
        title: 'Send a test message',
        description:
          'Every row has a Test button, so you can watch a sample message land in your channel before anything goes live.',
      },
      {
        type: 'new',
        title: 'Get pinged when a deal needs quoting',
        description:
          'A new optional update posts to Slack when a deal moves into Quoting — turn it on from the same page if you want it.',
      },
    ],
  },
  {
    id: '2026-08-10-mentions-rework',
    date: '2026-08-10',
    title: 'Mentions: open it and it clears',
    tagline:
      'Tags mark themselves read when you go and look, double-tags are gone, and anything you have read drops into "Earlier".',
    items: [
      {
        type: 'improved',
        title: 'Opening the thing marks the tag read',
        description:
          'Clicking "View fault" (or a deal, task, or service request) now counts as reading the tag — you no longer have to press "Mark read" separately. It works however you got there, so opening the fault from the list clears it too.',
      },
      {
        type: 'improved',
        title: 'All the tags on one job clear together',
        description:
          'If three people tagged you on the same fault, opening it once clears all three instead of leaving the other two sitting there.',
      },
      {
        type: 'new',
        title: '"Earlier" keeps the read ones out of the way',
        description:
          'Your mentions now split into new ones at the top and an "Earlier" group underneath, collapsed. Nothing is deleted — the count only tracks what you have not looked at yet, and "Clear read" empties the older group when you want it gone.',
      },
      {
        type: 'fixed',
        title: 'No more being tagged twice for one note',
        description:
          'Writing "@casey" twice in one note, or naming someone by first and last name, used to send two notifications. Fault call notes were sending two as well. One note now means one tag.',
      },
    ],
  },
  {
    id: '2026-08-10-plan-previews',
    date: '2026-08-10',
    title: 'See, edit and replace project plans',
    tagline:
      'Plan cards in Projects now show the actual drawing — and you can manage plans without re-uploading from scratch.',
    items: [
      {
        type: 'new',
        title: 'Plan cards show a real preview',
        description:
          'Every plan card now shows the first page of the PDF instead of a grey icon. Click the picture for a full-screen preview with zoom — no need to open the editor just to look at a drawing.',
      },
      {
        type: 'new',
        title: 'The preview shows everything you placed',
        description:
          'Access points, keypads, speakers, cable lines and zones all show on the preview exactly as they do in the editor. Click any one of them to see its label, status and notes. Use the "Markup" button at the top to hide them if you just want the plain drawing.',
      },
      {
        type: 'new',
        title: 'Replace a plan when a new drawing arrives',
        description:
          'Got a revised floor plan from the builder? Use "Replace PDF" on the plan card. The old version is kept as history, and your placed symbols and lines can carry across to the new drawing.',
      },
      {
        type: 'new',
        title: 'Edit plan details and move plans between dwellings',
        description:
          'Rename a plan, change its type or floor, or attach it to a different dwelling (or back to project-wide) from the new Edit details option — no more delete-and-re-upload.',
      },
      {
        type: 'improved',
        title: 'Mark old plans as old — and bring them back',
        description:
          'Mark a plan as old to move it out of the active list without losing it. "Show old plans" reveals them again, where you can preview, download, or restore any old version.',
      },
      {
        type: 'new',
        title: 'Group plans that belong together',
        description:
          'Eight plans that are all the same house — floor plan, lighting plan, furniture plan — can now live in one named group, like "Unit 3 — Level 1". Pick a group (or create one) when uploading or editing a plan, and the Plans tab shows each group as its own section you can collapse.',
      },
    ],
  },
  {
    id: '2026-08-10-timesheet-clarity',
    date: '2026-08-10',
    title: 'Timesheets now show how the hours add up',
    tagline:
      "A 7:30–3:30 day showed as 7h 30m with nothing explaining the missing half hour. Now the whole sum is on the card.",
    items: [
      {
        type: 'improved',
        title: 'Booked-on and booked-off times are front and centre',
        description:
          'Each day shows the actual clock-on and clock-off times in plain am/pm, with the working underneath: 8h on the clock, minus the 30-minute unpaid lunch, equals 7h 30m paid.',
      },
      {
        type: 'improved',
        title: 'Paid, standard day and time-in-lieu side by side',
        description:
          "Three boxes on every day so you can see at a glance whether it landed on the standard day, ran over, or came up short — and by how much.",
      },
      {
        type: 'improved',
        title: 'Changes to a day read like a sentence',
        description:
          'If someone fixed a clock-off or typed the hours in by hand, the day says so: what changed, from what to what, the reason they gave, who did it and when.',
      },
      {
        type: 'new',
        title: '"How these hours are worked out"',
        description:
          'A short explainer at the top of the timesheets page covering the unpaid lunch, the paid afternoon break, and which way banked and to-make-up run.',
      },
    ],
  },
  {
    id: '2026-08-09-pm-meeting-queue',
    date: '2026-08-09',
    title: 'PM meetings sit at the top of the schedule queue',
    tagline:
      'The rough size you pencil at Won now shows up for the scheduling team — and you can block the days out before the meeting happens.',
    items: [
      {
        type: 'new',
        title: 'PM meetings come first in the queue',
        description:
          'The scheduling queue now has a "PM Meetings — book first" section pinned at the top. Nothing else on that job can be booked until the meeting runs, so it stops getting lost among the other cards.',
      },
      {
        type: 'new',
        title: 'Block out the tentative days',
        description:
          'The PM card shows the rough size the office guessed ("~2 days on site, 2 techs"). Hit "Hold tentative days", pick a start date and the techs, and it drops orange tentative slots on the calendar — weekends skipped. When the real job appears you drag it straight onto the held slot.',
      },
      {
        type: 'fixed',
        title: 'Techs picked in a PM meeting are a suggestion, not a booking',
        description:
          'Picking techs during a PM meeting used to quietly assign them to the job while it was still sitting unbooked. Now they show up as "Preferred Tech" on the job card, so whoever schedules it can see who it should go to.',
      },
    ],
  },
  {
    id: '2026-08-07-deal-clarity',
    date: '2026-08-07',
    title: 'Deals: no more mystery moves',
    tagline:
      'Every button on a deal now tells you what it will do before it does it — and the automatic jumps explain themselves.',
    items: [
      {
        type: 'improved',
        title: 'Moving a deal always asks first',
        description:
          'Completing intake, finishing a pre-site, approving for billing and completing a deal now show a quick confirmation that spells out what happens next. Approving for billing also takes an optional note for the billing team — site variations, extra charges, anything they should know.',
      },
      {
        type: 'new',
        title: 'Rough estimate for PM-meeting jobs',
        description:
          'Flagging "Needs a PM meeting first" now asks for a rough size — days on site, techs, target window. It shows on the PM card and the provisioning board so scheduling can pencil a tentative hold while the meeting is being organised. Skipping it is fine.',
      },
      {
        type: 'new',
        title: 'Automatic steps are labelled',
        description:
          'Stages that move on their own (proposal sent, customer signs, install completed, gear arrives) now show a small "Automatic step" note on the deal, so nobody is left wondering why a deal jumped.',
      },
      {
        type: 'improved',
        title: 'Tidier deal sidebar',
        description:
          'The proposal-PDF upload box no longer shows on every deal, the install briefing card stopped nagging (the briefing is already required when dispatching), and the install plan now lives inside the Site Visits panel instead of its own near-duplicate card.',
      },
    ],
  },
  {
    id: '2026-08-07-team-schedule',
    date: '2026-08-07',
    title: 'Team Schedule: tap a job, get the whole job',
    tagline:
      'Tapping a teammate\'s job used to give you a name, an address and a phone number. Now it gives you everything they see — brief, notes, gate codes, gear, photos, and what happened.',
    items: [
      {
        type: 'improved',
        title: 'The full job, not a snippet',
        description:
          'Tapping any job on the team schedule opens the whole thing: the office brief and every note, what\'s booked versus how long they\'ve actually been on site, the crew (with a button to ring the tech, not just the customer), gate codes, WiFi and site logins, the kit picked for the job, photos, and the on-site log. On a finished job you also get the parts that went in, the ones that didn\'t, and the tech\'s wrap-up in their own words. Still read-only — you can\'t change anyone\'s job from here.',
      },
      {
        type: 'new',
        title: 'Swipe between days, and jump straight to one',
        description:
          'A row of dates sits under the header — tap any day to go there. Or just swipe the list left and right. "Today" is one tap from wherever you end up.',
      },
      {
        type: 'new',
        title: 'Search and filter the day',
        description:
          'Search a customer, a suburb or a tech. Searching a tech\'s name shows their whole day; searching a customer shows just that job, whoever is on it. Filter down to "on a job" to see who is actually out working right now, or "my crew" for your own.',
      },
      {
        type: 'improved',
        title: 'Easier to read on a phone',
        description:
          'Bigger rows and bigger text, the job type spelled out instead of just a coloured stripe, and how long each job runs next to the time. Anything worth knowing before you tap — an SOS, a flag, a return visit, a request for more time — shows as an icon on the row.',
      },
      {
        type: 'fixed',
        title: 'Less scrolling past things you don\'t need',
        description:
          'A whole-team event like the toolbox talk used to print on every single person\'s card. It now shows once at the top. Techs with nothing booked collapse into one line at the bottom instead of a column of empty cards.',
      },
    ],
  },
  {
    id: '2026-08-07-field-briefing-everywhere',
    date: '2026-08-07',
    title: 'Every job that goes to the field now carries a briefing',
    tagline:
      'Write the "do this, do that" once when you dispatch — it\'s the first thing the tech reads on the job.',
    items: [
      {
        type: 'new',
        title: 'The briefing prompt comes up when you send work out',
        description:
          'Schedule & Provision, Provision First, Gear Already Here, sending a fault to deployment, or raising a job from a fault or service request — each one now asks for the field briefing before it lets you finish. No more remembering to fill in the sidebar card afterwards.',
      },
      {
        type: 'new',
        title: 'Faults and service requests have briefings too',
        description:
          'Until now only deals had one. A fault heading to site arrived with an investigation trail and nothing telling the tech what to actually do. Now they carry the same briefing deals do.',
      },
      {
        type: 'improved',
        title: 'It\'s the first thing the tech sees',
        description:
          'On the job card the briefing sits right under what\'s wrong, above the notes — so the instruction comes first and the background is there underneath when they want it.',
      },
      {
        type: 'improved',
        title: 'Notes boxes now say what they\'re for',
        description:
          'The deployment notes box is "Access & safety" (it becomes the warning banner on the job) and the deal one is "Scheduling notes" (logistics for whoever books it). The briefing is the part the tech acts on.',
      },
    ],
  },
  {
    id: '2026-08-07-visit-report',
    date: '2026-08-07',
    title: 'The job write-up is gone. You can now see what happened instead',
    tagline:
      'No more wall of AI text at the end of a job. The deal shows the parts that went in, the ones that did not, the time on site, who went — and the tech’s notes exactly as they wrote them.',
    items: [
      {
        type: 'new',
        title: 'See which parts actually went in',
        description:
          'Every job now shows the kit it was sent out with, ticked off one by one. Anything the tech did not install is greyed out and marked "Not installed", and anything they grabbed on the day is marked "Added on site" with the reason. No more opening the job to find out.',
      },
      {
        type: 'new',
        title: 'Time on site, who went, and photos at a glance',
        description:
          'A row of numbers at the top of every completed visit: hours on site against what was booked, parts in, photos, and log entries. On a multi-visit deal you get the running total across all of them.',
      },
      {
        type: 'improved',
        title: 'Notes are the tech’s words again',
        description:
          'Wrap-up notes and the field log now show exactly as typed — nothing rewritten or padded out. Same for pre-sites.',
      },
      {
        type: 'improved',
        title: 'Faults keep their AI write-up, but only where it helps',
        description:
          'If a tech kept a field log on a callout, the AI still turns it into a readable summary — that is the one place it beat reading the raw log. Everywhere else, and on faults with no log, you just get the notes.',
      },
      {
        type: 'improved',
        title: 'Techs get a recap before a job closes',
        description:
          'Instead of proof-reading a paragraph, techs now see what they recorded — parts, time, photos, their note — and confirm it looks right. If they never ticked off the gear, it tells them.',
      },
    ],
  },
  {
    id: '2026-08-07-fault-card-declutter',
    date: '2026-08-07',
    title: 'Fault cards are readable again',
    tagline:
      'A fault used to stack up to seven coloured warning bars on one card. Now it shows the four things you need at a glance, and the rest is one click away.',
    items: [
      {
        type: 'improved',
        title: 'One glance tells you who, what, what is holding it up, and where it is up to',
        description:
          'The client, how urgent it is, the problem, and the stage sit across the top. Warnings like "5th fault in 60d" or "no update in 7d" are now small tags in a single row instead of a stack of full-width bars.',
      },
      {
        type: 'improved',
        title: 'The long stuff moved behind a "Details" link',
        description:
          'Intake notes, the tech\'s report, the field notes and the return-visit reason are still on the card — click Details to open them. You now get the full intake note and up to five field notes instead of a cut-off preview.',
      },
      {
        type: 'fixed',
        title: 'No more saying the same thing twice',
        description:
          'A finished job used to show both "Job Completed" and "Job Created" for the same visit, and the stage and priority appeared twice on every card. Each is stated once now.',
      },
    ],
  },
  {
    id: '2026-08-07-fault-notes-for-techs',
    date: '2026-08-07',
    title: 'Techs can finally see the office notes on a fault',
    tagline:
      'Everything the office logs on a fault ticket now reaches the tech on the job — live, in one place.',
    items: [
      {
        type: 'fixed',
        title: 'Office fault notes now reach the field',
        description:
          'Remote logs, call notes and client updates added to a fault ticket were not showing on the tech\'s job card — the card was reading a snapshot taken when the job was booked, so anything the office added afterwards never arrived. The job card now reads the fault ticket live, so a note added five minutes ago is on the tech\'s phone.',
      },
      {
        type: 'improved',
        title: 'One "Fault notes" section instead of notes everywhere',
        description:
          'A fault job used to show the same information in five competing blocks. There is now one section with the three most recent notes and a "View all" that opens the full history. The reported fault sits on its own line at the top.',
      },
      {
        type: 'improved',
        title: 'No AI brief on fault jobs',
        description:
          'Fault and maintenance job cards no longer show the AI-written brief or the "What should I know?" read. Techs see exactly what the office actually wrote. Install jobs are unchanged.',
      },
      {
        type: 'fixed',
        title: 'The Notes button count is honest again',
        description:
          'The number on the Notes button was missing the fault ticket\'s own notes, so it read lower than what you found inside.',
      },
    ],
  },
  {
    id: '2026-08-07-confirm-sms-merge',
    date: '2026-08-07',
    title: 'You choose who gets the confirmation text — and you can merge duplicate jobs',
    tagline:
      'Marking a job Confirmed now opens a quick check: who it goes to, and exactly what it says. Plus, two jobs for the same customer can be folded into one.',
    items: [
      {
        type: 'new',
        title: 'Send the confirmation to whoever is actually on site',
        description:
          'The job might be booked under Tom Lane, but if the builder is the one meeting you there, pick them instead. The modal lists every number on the job and on the customer record, and you can type a different one in.',
      },
      {
        type: 'new',
        title: 'See and edit the text before it sends',
        description:
          'The message comes pre-filled with the date, time and the tech who is going. Change any of it if the job needs a note, and the character/segment count updates as you type.',
      },
      {
        type: 'new',
        title: 'Confirm without texting',
        description:
          'Already spoke to them on the phone? Flick the toggle off and the job is marked confirmed with no text sent.',
      },
      {
        type: 'new',
        title: 'Merge two jobs into one',
        description:
          'Ended up with two jobs for the same customer and notes spread across both? Right-click the one you want to keep and choose "Merge another job in". Every note from the other job is copied across — nothing is overwritten — and the spare job is archived, not deleted, so you can undo it.',
      },
      {
        type: 'new',
        title: 'See what a merge will do before it happens',
        description:
          'The merge window shows exactly what moves across and warns you first — different days, a different address, techs that would be unassigned, or a customer who has already been sent a confirmation for the job you are folding away.',
      },
    ],
  },
  {
    id: '2026-08-07-billing-line-items',
    date: '2026-08-07',
    title: 'Send a fault or request to billing as real line items',
    tagline:
      'Build the invoice while the job is fresh — parts, labour, credits, and GST exactly how you want it.',
    items: [
      {
        type: 'new',
        title: 'Line items instead of one lump amount',
        description:
          'Sending a fault or a service request to billing now opens a proper line-item editor: a row per thing you\'re charging for, with quantity, unit price and a running total. The old single "invoice amount" box is gone — the total adds itself up.',
      },
      {
        type: 'new',
        title: 'Add GST, remove GST, or bill a line GST-free',
        description:
          'Choose whether your prices exclude GST, already include it, or whether the whole job is GST-free — the totals update as you switch. Individual lines can be flipped GST-free on their own for things like government levies.',
      },
      {
        type: 'new',
        title: 'Pull straight from the Parts Hub',
        description:
          'Add parts from the catalog with their sell prices already filled in, or type your own custom lines. There\'s a credit button too, for goodwill discounts and warranty offsets.',
      },
      {
        type: 'new',
        title: 'One-click prefill from what the job already knows',
        description:
          'Faults offer the materials the tech recorded and the labour time that built up on the ticket. Service requests offer the scope lines you already quoted, with one-off and monthly charges kept separate.',
      },
      {
        type: 'improved',
        title: 'The billing page shows what you actually charged',
        description:
          'Your line items and the exact GST you captured now appear on the billing card, and a job billed GST-free no longer has GST added back onto the page totals.',
      },
    ],
  },
  {
    // Same-day release — the id carries a suffix (it's the seen-tracking key
    // useWhatsNew indexes by) while `date` stays the plain ship date.
    id: '2026-08-07-job-address',
    date: '2026-08-07',
    title: 'Send a job to a different address',
    tagline: 'For when the job moves but the customer hasn’t.',
    items: [
      {
        type: 'new',
        title: 'Change a job’s address without touching the customer',
        description:
          'Click the address on a job in the scheduler or the jobs list and point it wherever the work is actually happening — the new place they just bought, the holiday house, a temporary site. The customer’s saved address and all their other jobs stay exactly as they were. If you want the new address kept for next time, there’s a tick box for that, and it’s off unless you ask.',
      },
      {
        type: 'new',
        title: 'The tech gets sent to the right house',
        description:
          'The address change flows through everything the old notes-in-the-job trick missed: the tech’s navigation link, the "on my way" text to the customer, the drive-time estimate and the pin on the office map. The job card also shows an amber "one-off site" flag with your reason, so nobody who knows the customer drives to the address they remember.',
      },
      {
        type: 'improved',
        title: 'One tap to put it back',
        description:
          'A moved job remembers the address it started with. Open the address again and "Put it back" returns it — no retyping, and no guessing which address was the real one. Every change is recorded on the job with who made it and why.',
      },
    ],
  },
  {
    id: '2026-08-07-site-visits',
    date: '2026-08-07',
    title: 'Every site visit, right there on the deal',
    tagline: 'See the days you were on site, how long for, and jump straight to that day in the scheduler.',
    items: [
      {
        type: 'new',
        title: 'A Site visits list on every deal',
        description:
          "Deals now show every visit — installs, pre-sites, PM meetings and follow-up tasks — in date order, with who was on it and how it went. It stays on the deal for good, so you can still see the run of days on a job that's finished and gone to billing.",
      },
      {
        type: 'fixed',
        title: 'The scheduler link takes you to the right day',
        description:
          '"Open in scheduler" used to drop you on today no matter which visit you were looking at. Tap any visit and you land on that day with the job already open.',
      },
      {
        type: 'new',
        title: 'Hours on site, and a way to fix them',
        description:
          "Each visit shows the time the tech's timer recorded, with a running total for the whole deal. If a timer was left running or never started, hit Fix and put the real number in — it keeps a note of who changed it and why, and the tech's original timer is kept on the record.",
      },
      {
        type: 'fixed',
        title: 'Finished visits no longer look unscheduled',
        description:
          'A visit the tech closed out as needing a return trip was showing as if it had never happened. It now reads "Return needed", and a visit running right now says so too.',
      },
    ],
  },
  {
    // Six releases shipped on 2026-08-07. The id is the seen-tracking key
    // (useWhatsNew indexes by it), so same-day releases carry a suffix while
    // `date` stays the plain ship date and the card still reads "7 August".
    id: '2026-08-07-create-menu',
    date: '2026-08-07',
    title: 'Start a deal, fault or service request straight from the customer',
    items: [
      {
        type: 'new',
        title: 'Service requests can now be raised from the customer record',
        description:
          "Open a customer, hit New, and pick Service request — plan changes, relocations and account updates no longer mean leaving the record and searching for them again on the Service Requests page.",
      },
      {
        type: 'improved',
        title: 'One New button instead of a row of buttons',
        description:
          'Deal, Fault and Service request now sit together under a single New menu at the top right of the customer panel, each with a line explaining when to pick it. Call, SMS, Email and Log Call stay where they were.',
      },
      {
        type: 'improved',
        title: 'What you just created shows up straight away',
        description:
          "After you raise a deal, fault or service request the customer's Activity tab opens with it already in the timeline — no closing and reopening the record to see it.",
      },
    ],
  },
  {
    id: '2026-08-07-pinned-notes',
    date: '2026-08-07',
    title: 'Pinned notes actually stay pinned',
    tagline: 'If you pinned it, the field sees it.',
    items: [
      {
        type: 'fixed',
        title: 'Pinned deal notes now reach the tech',
        description:
          "When you pinned a note on a deal, it wasn't making it onto jobs created afterwards — so the tech's job brief showed no pinned notes at all. Pinned notes now carry through to the job brief, the job insights and the tech's job card.",
      },
      {
        type: 'fixed',
        title: 'A pinned note is no longer buried on the scheduling queue',
        description:
          'Unscheduled job cards show one line of notes, and it was always the most recent one — so a pinned note got pushed out by any later chat on the deal. The pinned note now wins that spot, marked with a pin.',
      },
      {
        type: 'fixed',
        title: 'The pin no longer disappears on repeated notes',
        description:
          "When the same note text was saved from two places, the tech's brief kept one copy — and sometimes that was the un-pinned one, so the note lost its pin highlight and could drop off a busy brief entirely. It now keeps the pin whichever copy it came from.",
      },
      {
        type: 'fixed',
        title: 'Pinned notes show on the scheduler deal preview again',
        description:
          'Previewing a job\'s deal has a "Pinned Notes" section that had gone empty for anything pinned recently. It shows them again — and previewing two deals quickly no longer risks showing the first one\'s details under the second.',
      },
      {
        type: 'improved',
        title: 'Pinning a note now reaches jobs that already exist',
        description:
          "The tech's job brief used to only know about notes pinned before the job was created. It now reads pins live, so pinning something today shows up on jobs that were already booked.",
      },
    ],
  },
  {
    id: '2026-08-07',
    date: '2026-08-07',
    title: 'Customer addresses: delete them, tag them, fix them',
    tagline: 'Old addresses now stay deleted, and you can label what kind of place each one is.',
    items: [
      {
        type: 'fixed',
        title: 'Deleted addresses stay deleted',
        description:
          "Removing an old address used to look like it worked, then the address came back the next time you opened the customer — because we were re-reading it off their linked services every time. Now when you remove one, it's gone for good. Adding it again by hand brings it back if you change your mind.",
      },
      {
        type: 'new',
        title: 'Tag an address so you know which place it is',
        description:
          'Every address can be tagged — Holiday, Investment, Rental, Business, Family, Site, Billing, Postal, Storage — or type your own, like "Brother\'s place". The tags show up on the customer, and when you pick a site while adding a service, so you are never guessing which of their four addresses is the holiday house.',
      },
      {
        type: 'new',
        title: 'Edit an address instead of deleting and re-adding it',
        description:
          'Addresses now have an edit button. Fix a typo, rename it, or change its tags without losing the entry. There is also a search-as-you-type box for the street, so the suburb, state and postcode fill themselves in.',
      },
      {
        type: 'improved',
        title: '"Primary" is now called "Main"',
        description:
          "The customer's default address is labelled Main, and setting it also updates the address shown at the top of their record — so the headline address and the address book can no longer disagree.",
      },
    ],
  },
  {
    id: '2026-08-06',
    date: '2026-08-06',
    title: 'Email sign-in fixed on iPhone',
    tagline: 'The code and the link both work now, wherever you open your email.',
    items: [
      {
        type: 'fixed',
        title: 'The sign-in link works from any mail app',
        description:
          "Tapping the link from Mail, Gmail or Outlook on an iPhone used to fail with a confusing error. It now signs you straight in when it can, and when your mail app opens the link somewhere else it offers you a one-tap way to sign in right there instead.",
      },
      {
        type: 'fixed',
        title: 'The keyboard no longer covers the 6-digit boxes',
        description:
          'On iPhone the on-screen keyboard could sit on top of the code boxes with no way to scroll to them. The screen now shrinks to fit above the keyboard.',
      },
      {
        type: 'improved',
        title: 'Your code screen waits for you',
        description:
          "Leaving the app to go read your email used to drop you back at the profile picker with nowhere to type the code. You now come back to exactly where you left off, so one email is all you need.",
      },
      {
        type: 'improved',
        title: 'A proper Verify button',
        description:
          "The code used to submit itself only once all six boxes were perfectly filled — if one didn't take, nothing happened. There's now a Verify code button you can tap.",
      },
    ],
  },
  {
    id: '2026-08-04',
    date: '2026-08-04',
    title: 'Time-in-lieu says who owes whom',
    items: [
      {
        type: 'improved',
        title: 'Your TIL balance now says which way the time goes',
        description:
          'The jar used to read "4h 30m ahead" or "behind", which left people guessing who owed the time. It now says "banked" when you\'ve worked extra — that time is yours to take off — and "to make up" when you\'ve taken more off than you\'ve earned.',
      },
    ],
  },
  {
    id: '2026-08-02',
    date: '2026-08-02',
    title: 'The Admin page finally makes sense',
    tagline:
      'Everything is grouped, named in plain English, and the internal plumbing is out of your way.',
    items: [
      {
        type: 'improved',
        title: 'Admin is organised into sections',
        description:
          'Instead of one long wall of tiles, Admin is now grouped — business records, team, Otis, customer communications, integrations, and workspace — with a short line under each heading explaining what lives there.',
      },
      {
        type: 'improved',
        title: 'Every tile says what it actually does',
        description:
          'Descriptions were rewritten in plain English. No more internal project names, "Phase 2" jargon, or greyed-out "coming soon" tiles that never did anything.',
      },
      {
        type: 'improved',
        title: 'Internal tools moved out of the way',
        description:
          'One-time migration and developer tools now live on their own page that only platform admins can see, so the everyday Admin page only shows things you would actually use.',
      },
      {
        type: 'improved',
        title: 'Admin tiles are real links now',
        description:
          'Middle-click or Ctrl/Cmd-click any admin tile to open it in a new tab — handy when you want, say, Customers and Services open side by side.',
      },
      {
        type: 'fixed',
        title: 'Your theme choice now applies everywhere',
        description:
          "Some highlight colours were following your device's dark/light setting instead of the app's own theme toggle. If your phone or computer was set differently to the app, badges and warnings could show hard-to-read colours — they now always match the theme you picked in the app.",
      },
    ],
  },
  {
    id: '2026-08-01',
    date: '2026-08-01',
    title: 'A clearer job screen, and one briefing for every install',
    tagline: 'Open a job and it tells you what you are there to do — one briefing instead of four competing notes, and a header that puts the customer first.',
    items: [
      {
        type: 'new',
        title: 'Install jobs open with a field briefing',
        description:
          'The top of an install job now answers the big questions straight away: was there a pre-site, who did it and when, what they said the install crew should do, and what they scoped. No pre-site? It shows the office briefing instead, plus who ran the deal so you know who to ask.',
      },
      {
        type: 'new',
        title: 'Pre-sites capture a second note just for the install techs',
        description:
          'When you finish a pre-site, your write-up still goes to the office for quoting — and now there is a second box for what the install crew should actually do on site. Small job? Write it up there and then. Too big for a note? Tick "easier to run a PM meeting first" and the office sees it at scheduling.',
      },
      {
        type: 'new',
        title: 'Office can brief jobs that skip pre-site',
        description:
          'Deals that go straight from quote to install get an "Install briefing" card in the deal workspace once the proposal is accepted. It nudges until someone writes exactly what the techs should do — before the job is scheduled, not after the tech is standing on site confused.',
      },
      {
        type: 'improved',
        title: 'The job screen got a big clean-up',
        description:
          "The customer's name is big and never cut off, gate codes and dog warnings show in full instead of being clipped, and all the briefing content now lives in one card instead of four competing ones. The SOS, flag and more-time buttons moved behind a single life-ring button — SOS is still one tap away, it just doesn't shout at you all day.",
      },
      {
        type: 'fixed',
        title: 'Small annoyances on the job screen',
        description:
          'One camera button (it asks take-or-upload), one job-log box on faults instead of two, the "1 more needed" log counter that no longer meant anything is gone, Starlink photo slots only appear once you\'re on site, and finished jobs no longer show a dead grey button.',
      },
    ],
  },
  {
    id: '2026-07-28',
    date: '2026-07-28',
    title: 'Job cards tell you what the job actually is',
    tagline: 'The service is on the card and in the header, and a short brief up top covers the things you need to know before you get there.',
    items: [
      {
        type: 'new',
        title: 'The service now shows next to the job type',
        description:
          'Jobs read as "Install · Starlink" instead of just a customer name — on your day list and in the header at the top of the job, so it stays on screen while you scroll.',
      },
      {
        type: 'new',
        title: 'A short brief at the top of every job',
        description:
          'Up to three lines covering the things that change what you do: where the customer wants gear put, gate codes and dogs, and on a fault, what has already been tried. If there is nothing worth flagging it stays out of your way.',
      },
      {
        type: 'improved',
        title: 'Job preview tucked away until you want it',
        description:
          'The full spec sheet is now one tap below the brief instead of the first thing you scroll past. Nothing was removed — and if there is no brief for a job, it opens as it always did.',
      },
    ],
  },
  {
    id: '2026-07-20',
    date: '2026-07-20',
    title: 'Field-job reminders, proposal discounts and clearer terms',
    tagline: "A heads-up when you're on a field job, a percentage off a proposal, and terms customers actually read.",
    items: [
      {
        type: 'new',
        title: "Get a reminder when you're on a field job",
        description:
          "If you're office-based but get pulled onto a job in the field, the app can now text and email you a heads-up — the evening before (\"you're on a field job tomorrow\") and again that morning (\"reminder: field job today\"). It's off by default; an admin switches it on per person under Settings → Team.",
      },
      {
        type: 'new',
        title: 'Add a discount to a proposal',
        description:
          "You can now take a percentage off a proposal's one-off price (labour and items). The customer sees the original price struck through, exactly what they save, and the discounted total — and they're invoiced the discounted amount, so what they sign is what they pay. Monthly plans aren't affected.",
      },
      {
        type: 'improved',
        title: 'Clearer terms & conditions at signing',
        description:
          'Your terms now sit in a wider, easier-to-read panel, and the customer reads them and ticks to confirm before they can sign — so acceptance is on the record.',
      },
      {
        type: 'fixed',
        title: 'The proposal Send button is back',
        description:
          "Some deals weren't showing a way to send the proposal. The builder now has a clear \"Send to customer\" button whenever a proposal is ready to go.",
      },
      {
        type: 'fixed',
        title: "Monthly price is right on “pick one” plans",
        description:
          "When a proposal offers a choice of monthly plans as a “pick one” section, the deal was adding every option together in the monthly total. It now shows just the single default plan — matching what the customer sees and signs for.",
      },
      {
        type: 'improved',
        title: 'Re-raise a PM meeting from any deal',
        description:
          "Once a project planning meeting is done or skipped, you can now schedule another one straight from the deal — either from the PM Meeting card or the deal Actions menu — without having to revert the deal's stage. Good for when scope shifts after the first meeting and the crew needs to re-plan.",
      },
    ],
  },
  {
    id: '2026-07-19',
    date: '2026-07-19',
    title: 'A round of reliability fixes',
    tagline: 'Fixes from a full review of flagged issues — clock-out, sign-in and billing.',
    items: [
      {
        type: 'new',
        title: 'Find UniFi consoles from search',
        description:
          "Search now looks inside the customer's Site Vault — great for tracking down a UniFi console when you only know its name. Type a UniFi console or site name, an NVR name, a WiFi network, or any other detail saved in the vault and the matching customer comes straight up. Results still only show the customer; the saved details stay behind tap-to-reveal on their record.",
      },
      {
        type: 'fixed',
        title: "Clock-out tells you if it didn't save",
        description:
          "If a clock-out can't be saved — bad signal, or a time that doesn't add up — you'll now see the error instead of a screen that says you're clocked out while the timesheet quietly stays open.",
      },
      {
        type: 'fixed',
        title: 'Provisioning staff land in the right place after signing in',
        description:
          'Signing in as a provisioning user now takes you straight to the Ops board instead of leaving you stuck on the sign-in screen.',
      },
      {
        type: 'fixed',
        title: 'Sending a fault to billing is more reliable',
        description:
          'Fixed an error that could stop certain fee-for-service faults from being sent to billing.',
      },
      {
        type: 'fixed',
        title: 'Site access codes are safe when a tech captures WiFi',
        description:
          "Gate codes, alarm codes and other logins saved on a customer's Site Vault are no longer wiped when a tech records fresh WiFi on a job — the two are merged instead of one replacing the other.",
      },
      {
        type: 'fixed',
        title: "Editing a job's techs no longer drops a change",
        description:
          'When two people add or remove technicians on the same job at the same moment, both changes now stick — one could previously overwrite the other.',
      },
      {
        type: 'fixed',
        title: 'Fault history keeps every entry',
        description:
          'Notes and status changes on a fault are no longer at risk of being lost when two people work the same fault at once.',
      },
    ],
  },
  {
    id: '2026-07-17',
    date: '2026-07-17',
    title: 'Send proposals in one place, tidier services and scheduling',
    tagline: 'A cleaner proposal send flow, plus a big pass on Customer Services, the scheduler, faults and requests.',
    items: [
      {
        type: 'improved',
        title: 'Build, edit and send a proposal from one screen',
        description:
          "The proposal builder now has its own \"Send to customer\" button — no more bouncing between the deal card and the builder. It freezes a fresh snapshot of the customer's details right before sending, and \"Update customer's copy\" refreshes an already-sent proposal in place.",
      },
      {
        type: 'improved',
        title: 'Customer Services is easier to work with',
        description:
          'Cancelled services are hidden by default so the list shows what\'s actually live (still one click away). Editing a service now offers a plan dropdown that updates the price with it, adding an NBN service filters plans to the technology you picked, and cancelling asks for a date and reminds you with a to-do. A new "Moving Out" option keeps a service\'s history for the next occupant instead of losing it. Also fixed a bug that could create the same service twice.',
      },
      {
        type: 'fixed',
        title: 'Scheduler polish — texts, resizing, and busy days',
        description:
          "Rescheduling a job now texts the customer the correct new time (it was sometimes keeping the old time with the new date). Unscheduling a job clears its old \"confirmed\"/\"no answer\" tag. Short 15-minute jobs can be resized again, double-booked slots now line up side-by-side instead of overlapping, and vehicle services no longer crowd out real jobs on the board. Internal meetings between office staff also show up properly now.",
      },
      {
        type: 'improved',
        title: 'Faults and service requests, cleaned up',
        description:
          "Closing a fault no longer forces you to type notes first. The stage picker is snappier and stays anchored on screen. You can now delete a fault or service request outright when it was raised in error, and service requests have a new \"Relocating\" type for when a customer's moving. Faults and requests now share the same tidy set of actions — send to a deal, fault or request, or raise a job — and phone/email are tap-to-copy instead of launching your mail app.",
      },
      {
        type: 'new',
        title: 'Notes and a clearer history on the customer screen',
        description:
          "You can now jot a reference note straight onto a customer — it lands in their Activity timeline (filterable with the new Notes chip) alongside their jobs, deals, faults, calls and services, and reads like one clean story you can open full-screen. Editing a customer no longer makes the window vanish behind you.",
      },
      {
        type: 'improved',
        title: 'Action items you can actually work',
        description:
          "Open an action item to assign people, add tags and leave notes on a running timeline — and raise your own action items by hand. A return visit flagged in the field now becomes an action item automatically and stands out in the schedule queue with its own badge.",
      },
      {
        type: 'improved',
        title: 'A richer to-do list on your dashboard',
        description:
          'Your dashboard to-dos now show priority colours and note/assignee icons at a glance, and expand into the full task view — description, people, subtasks and comments — right from the card.',
      },
      {
        type: 'improved',
        title: 'See everything on the Billing page',
        description:
          'Billing has a new "All" tab that stacks To Bill, Awaiting Payment, Bad Debtors and Paid together so you can scan the whole picture without clicking between tabs.',
      },
      {
        type: 'fixed',
        title: 'The Customers page loads faster',
        description:
          'The Customers list now loads once and stays live, instead of re-fetching every customer each time you open it.',
      },
    ],
  },
  {
    id: '2026-07-16',
    date: '2026-07-16',
    title: 'Clearer proposal prices, emails that write themselves',
    tagline: 'GST spelled out on every proposal, emails that sign off for you, and Claude can do more.',
    items: [
      {
        type: 'improved',
        title: 'Every proposal price shows GST included',
        description:
          'On the proposal you build and the one the customer sees, every figure now shows the GST-inclusive price with a clear "inc GST" label — and product cards finally show their own price. While you\'re editing, each price field tells you exactly what the customer will see.',
      },
      {
        type: 'new',
        title: 'Proposal emails that adapt to the deal',
        description:
          "Your proposal email now fills in the service type automatically (Starlink, Surveillance, Smart Home…) and signs off with the sender's name and job title (set in Settings → Team). You can also write different email copy per deal type when you want it.",
      },
      {
        type: 'improved',
        title: 'Claude can now handle faults, requests and billing',
        description:
          'The Claude connector does more than jobs now — resolve a fault, move a service request along, or mark something billed, just by asking. Admins can also add it with only a link and their PIN, so there\'s no token to copy.',
      },
      {
        type: 'improved',
        title: 'PM meetings open on the plan, notes reach the office',
        description:
          "The project-management meeting screen now opens straight on the visit plan instead of the AI note-taker, and any notes taken in the meeting show up on the deal's timeline so the office sees them.",
      },
      {
        type: 'improved',
        title: 'A tidier office top bar',
        description:
          'What\'s New, bug reports, the daily dice and the quiz now tuck into a single "More" (•••) menu, so the top bar isn\'t fighting for space.',
      },
      {
        type: 'fixed',
        title: 'Raising a job on an older deal works again',
        description:
          'Fixed an error when raising or re-raising a job on a deal that had already been scheduled, plus a glitch that could block the call history on the customer screen.',
      },
    ],
  },
  {
    id: '2026-07-14',
    date: '2026-07-14',
    title: 'Chat to get things done, clearer timesheets',
    tagline: 'Connect Claude to the app, plus a friendlier email editor.',
    items: [
      {
        type: 'new',
        title: 'Connect Claude and run jobs by chatting',
        description:
          "You can now link Claude (the AI assistant on claude.ai or the desktop app) to your workspace and just tell it what you need — \"find today's jobs for Reuben\", or \"complete the Starlink job, I swapped the router\". Admins turn it on under Admin → Claude Connector.",
      },
      {
        type: 'improved',
        title: 'Time in lieu now reads in plain English',
        description:
          'Timesheets and time-in-lieu spell out what you\'re owed in words instead of plus/minus numbers you had to decode.',
      },
      {
        type: 'improved',
        title: 'Style your emails without touching code',
        description:
          'The invoice and proposal email editor has a proper formatting toolbar now — bold, links and lists with a click, no HTML required.',
      },
      {
        type: 'fixed',
        title: 'No more surprise admin lock-outs',
        description:
          'Fixed an intermittent glitch where directors (and tech accounts with admin rights) could be blocked from a couple of admin tools until they refreshed.',
      },
    ],
  },
  {
    id: '2026-07-03',
    date: '2026-07-03',
    title: 'On Your Plate, simpler faults, richer attachments',
    tagline: 'A personal task hub, a cleaner fault flow, and photos + video everywhere.',
    items: [
      {
        type: 'new',
        title: '“On Your Plate” — everything assigned to you, in one place',
        description:
          'Tasks, tags and reminders meant for you now live together. Open the "On Your Plate" card on your dashboard to see the full board, tick things off, and get a nudge when something\'s due.',
      },
      {
        type: 'new',
        title: 'Attach several photos — or a video — at once',
        description:
          'Drag a batch of photos, or a video, straight onto a deal, fault or job. Video attachments are supported for the first time.',
      },
      {
        type: 'new',
        title: 'Book internal meetings on the schedule',
        description:
          'Put an internal meeting on the schedule with tagged attendees, a time and a place — it shows up for everyone involved.',
      },
      {
        type: 'new',
        title: 'Cover photos and photo banners on proposals',
        description:
          'Add a big cover photo to a proposal, drop a full-width photo between sections, and set a hero image per scope and a photo per product. Proposals look the part.',
      },
      {
        type: 'improved',
        title: 'Faults are simpler and steadier',
        description:
          'The fault pipeline went from six stages down to four clear ones — Triage, Remote Support, Deploy and Review. Moving a fault between stages no longer sets off surprise actions; deployment, tech consults and billing are plain buttons now. Faults and service requests also share one consistent look.',
      },
      {
        type: 'improved',
        title: 'Signing a proposal is one clean screen',
        description:
          'The customer signing flow is shorter (Sign, then Review), defaults to a typed signature, and the "invisible signature" problem on the signed copy is fixed.',
      },
      {
        type: 'improved',
        title: 'Search that doesn\'t drop results',
        description:
          'Searching across customers, jobs and records is more consistent and no longer quietly leaves matches out.',
      },
      {
        type: 'fixed',
        title: 'Fault history shows on the tech job screen',
        description:
          'Techs now see the fault\'s investigation history on the job view, and a few date/time display glitches were tidied up.',
      },
    ],
  },
  {
    id: '2026-06-30',
    date: '2026-06-30',
    title: 'Deposits on won deals, GST made clear',
    tagline: 'Send a deposit invoice, and see GST-inclusive prices everywhere.',
    items: [
      {
        type: 'new',
        title: 'Prompt for a 50% deposit when a deal is won',
        description:
          'Win a deal and the app can offer to send a 50% deposit invoice — with a preview you can check and tweak before it goes out.',
      },
      {
        type: 'new',
        title: 'Techs plan the install in the PM meeting',
        description:
          'For jobs that need a project-management meeting, the techs now set the visits, crew and timing in the meeting itself — the office no longer has to schedule it up front.',
      },
      {
        type: 'improved',
        title: 'GST-inclusive prices, clearly labelled',
        description:
          'Proposals, quotes, invoices and receipts now show prices with GST included, and every figure is labelled so there\'s no second-guessing what a number means.',
      },
      {
        type: 'improved',
        title: 'One tidy Notes section on the field job view',
        description:
          'The tech job screen now has a single, time-ordered Notes section — with the AI read tucked inside it — instead of notes scattered across separate cards.',
      },
      {
        type: 'improved',
        title: 'Smarter provisioning for services and ISPs',
        description:
          'The Services & ISP tab now captures the right ordering details for new connections, porting and activations. Labour lines no longer clutter the provisioning board.',
      },
      {
        type: 'improved',
        title: 'Adjustable quantities on internal orders',
        description:
          'Set what\'s "needed" versus what\'s been "ordered" on internal stock orders. Lines clear themselves once fully ordered, and you can undo with one tap.',
      },
      {
        type: 'fixed',
        title: 'The signed copy shows the real signature',
        description:
          'Signed proposals now show the customer\'s actual signature on both the on-screen copy and the PDF. We also fixed a slow-signing timeout, kept won deals sitting at "Won" instead of skipping ahead, and made sure sending a deal back to scheduling always raises a job.',
      },
    ],
  },
  {
    id: '2026-06-24',
    date: '2026-06-24',
    title: 'A better proposal builder',
    tagline: 'Design proposals right on the page, and let jobs raise their own follow-ups.',
    items: [
      {
        type: 'new',
        title: 'Edit the proposal cover right on the page',
        description:
          'Style the cover in place — photo, title, tagline, colour and size — and add or paste a product photo onto any line item.',
      },
      {
        type: 'new',
        title: '“Pick one” plan options',
        description:
          'Offer choices where the customer picks just one (say, one internet plan). They choose, and the total updates live.',
      },
      {
        type: 'new',
        title: 'Save any proposal as a reusable template',
        description:
          'Built a proposal you like? Save it as a template — including a "Meet the team" section you can add and crop — and reuse it next time.',
      },
      {
        type: 'new',
        title: 'Jobs raise their own follow-up action items',
        description:
          'When a job wraps up, the app now pulls follow-ups out of the AI summary automatically so nothing slips. Find them under Operations → Action Items.',
      },
      {
        type: 'new',
        title: 'AV (Audio Visual) is now a service category',
        description:
          'You can pick AV as its own service category on a deal, alongside NBN, Starlink, Security and the rest.',
      },
      {
        type: 'new',
        title: 'Vehicle services land on the schedule',
        description:
          'Booking a vehicle service now drops it onto the assigned tech\'s schedule on the due date, so everyone can see when a vehicle is off the road.',
      },
      {
        type: 'improved',
        title: 'Labour shown to customers as a clean price',
        description:
          'Quotes show the customer a simple "Solution installation" line and a price, while the crew size and hours flow through to billing and the scheduler behind the scenes.',
      },
      {
        type: 'improved',
        title: '“What should I know?” briefing for techs',
        description:
          'Techs can tap "What should I know?" on a job and get a quick, plain-English brief pulled from the job\'s full history.',
      },
      {
        type: 'improved',
        title: 'Notes and timelines render formatting',
        description:
          'Pasted content, lists, bold text and tables now show up properly across deal and fault timelines instead of as raw text.',
      },
      {
        type: 'improved',
        title: 'Breaks captured at clock-off',
        description:
          'Clocking off now asks about your meal break and nets it out of your paid hours automatically.',
      },
      {
        type: 'fixed',
        title: 'Customer proposal view keeps everything',
        description:
          'The customer-facing proposal no longer drops product photos, recurring services or labour, and the preview fills the page properly.',
      },
    ],
  },
  {
    id: '2026-06-22',
    date: '2026-06-22',
    title: 'Meet Otis, and clock in from your phone',
    tagline: 'An AI assistant on every screen, time tracking, and smoother billing.',
    items: [
      {
        type: 'new',
        title: 'Otis — your AI assistant, on every screen',
        description:
          "A friendly assistant, Otis, now floats on every screen for office and field. Ask it things like \"have we been to this address before?\" or \"what's the gate code?\" — it searches years of old notes, past jobs and message history, plus what's happening live right now.",
      },
      {
        type: 'new',
        title: 'Clock in and out in the app',
        description:
          'Field staff can clock on and off from their phone, and time-in-lieu is tracked automatically so hours owed are always up to date.',
      },
      {
        type: 'new',
        title: 'OptiComm added as an internet service',
        description:
          'OptiComm is now a supported internet service type alongside NBN and Starlink, with its own account and order details.',
      },
      {
        type: 'improved',
        title: 'Add internet & phone plans during quote review',
        description:
          'Quote review can now add NBN, Starlink, VoIP and 4G plans with the full ordering details (supplier, plan, porting numbers) that flow straight through to the provisioner.',
      },
      {
        type: 'improved',
        title: '“Held by Customer” queue in the scheduler',
        description:
          "Park jobs that are waiting on the customer in their own section of the scheduler queue, so they don't clog up the active list — and drag them back onto the calendar when the customer's ready.",
      },
      {
        type: 'improved',
        title: 'More accurate refunds and receipts',
        description:
          'Refunds now correctly account for any earlier partial refunds, payment pages show the right tax label, and proposals and invoices print exactly as the customer sees them.',
      },
      {
        type: 'fixed',
        title: 'Paused jobs and re-raised faults behave',
        description:
          'A paused job is now clearly marked on the field view, re-raised faults show up in the pipeline again, and opening certain older fault tickets no longer crashes.',
      },
    ],
  },
  {
    id: '2026-05-24',
    date: '2026-05-24',
    title: 'Drag emails onto deals, attach files to requests',
    tagline: 'Keep the whole paper trail in one place — and rewind a fault if you mis-click.',
    items: [
      {
        type: 'new',
        title: 'Drag an email straight onto a deal',
        description:
          'Drop an email (.eml) from Gmail, Outlook or Apple Mail onto a deal and the whole message — plus its attachments — is saved to the timeline. No copy-paste.',
      },
      {
        type: 'new',
        title: 'Attach files to service requests',
        description:
          'Add photos, quotes, screenshots or documents to a service request so everything about a job lives with the request.',
      },
      {
        type: 'improved',
        title: 'Move a fault backwards to fix a mistake',
        description:
          'Faults can now move back a stage as well as forwards, so a wrong tap is a quick correction instead of a dead end.',
      },
      {
        type: 'improved',
        title: 'Starlink captures the account number',
        description:
          'Starlink services now record the customer-facing account number instead of a pile of individual serials — easier to look up and match to billing.',
      },
      {
        type: 'improved',
        title: 'Send a branded post-job report to the customer',
        description:
          'After a job, you can send the customer a branded PDF report with a photo gallery of the work.',
      },
      {
        type: 'improved',
        title: 'Edit line items after a deal is won',
        description:
          'Add or remove line items on a won deal — and removals now flow through to provisioning and billing so nothing is left stranded.',
      },
    ],
  },
  {
    id: '2026-05-13',
    date: '2026-05-13',
    title: 'Take payments online, and build proposals on the page',
    tagline: 'Customer payment pages, a single customer view, and a live proposal editor.',
    items: [
      {
        type: 'new',
        title: 'Customers can pay invoices online',
        description:
          'Send a branded invoice and the customer pays by card on a secure payment page — with automatic receipts once they do.',
      },
      {
        type: 'new',
        title: 'Customer 360 — one view of everything',
        description:
          "A customer's deals, jobs, faults, services, invoices and proposals are now cross-linked in one place, so you can see their whole history at a glance.",
      },
      {
        type: 'new',
        title: 'Edit your billing emails yourself',
        description:
          'A visual editor lets you customise the invoice, receipt and reminder emails customers receive — no code, with a live preview.',
      },
      {
        type: 'improved',
        title: 'Build proposals right on the page',
        description:
          'Edit product names, prices, scopes and labour directly on the live proposal preview, drag products between scopes, and press Cmd/Ctrl+K to add from the parts library.',
      },
      {
        type: 'improved',
        title: 'Redesigned, magazine-style invoices',
        description:
          'Invoices, receipts and payment pages got a clean, premium redesign that matches the look of your proposals.',
      },
    ],
  },
  {
    id: '2026-04-30',
    date: '2026-04-30',
    title: 'A new home dashboard, fleet management, and bundles',
    tagline: 'A live command centre, vehicle tracking, and flexible service pricing.',
    items: [
      {
        type: 'new',
        title: 'New home dashboard',
        description:
          'The home screen got a live "field" band showing what each tech is doing, an inbox that merges SOS alerts, flags, mentions and to-dos, and a stream of everything that happened since your last visit.',
      },
      {
        type: 'new',
        title: 'Vehicle & fleet management',
        description:
          'Track your vehicles — kilometres, services, bookings and compliance dates — with reminders before rego, insurance or a service falls due.',
      },
      {
        type: 'new',
        title: 'Service bundles',
        description:
          'Group several services into a single bundle with one combined monthly price.',
      },
      {
        type: 'new',
        title: 'Custom and grandfathered pricing',
        description:
          "Set a custom monthly price (and name) on any service, or push a plan price change out to everyone on the old price at once — while leaving grandfathered customers untouched.",
      },
      {
        type: 'improved',
        title: 'Return visits are easy to spot',
        description:
          'Follow-up visits now surface on the dashboard and the jobs list so nothing falls through the cracks.',
      },
      {
        type: 'improved',
        title: 'Call, text or email from the fault header',
        description:
          "One-tap call / SMS / email chips on a fault, straight to the linked customer.",
      },
    ],
  },
  {
    id: '2026-04-19',
    date: '2026-04-19',
    title: 'Faults redesigned, service requests, and global search',
    tagline: 'A cleaner fault flow, a home for customer change requests, and search across everything.',
    items: [
      {
        type: 'new',
        title: 'Redesigned fault tickets',
        description:
          'Faults got a timeline-first layout with a clear "next step", a right-hand action rail, and quick call/text/email — so it\'s obvious what to do next.',
      },
      {
        type: 'new',
        title: 'Service Requests',
        description:
          'A dedicated tracker for customer-initiated changes — speed swaps, VoIP edits, remote config work — with a one-click "Send to Field".',
      },
      {
        type: 'new',
        title: 'Global search',
        description:
          'One search bar across customers, jobs, deals and more, smart enough to work out what you typed (a phone number, an address, a name).',
      },
      {
        type: 'new',
        title: 'Employee of the Month',
        description:
          'Automated monthly voting with an anonymous reveal and a Hall of Fame of past winners.',
      },
      {
        type: 'new',
        title: 'Attach files anywhere',
        description:
          'Attach files to jobs, deals, customers and services — the paperwork lives with the record it belongs to.',
      },
      {
        type: 'improved',
        title: 'Proper light and dark modes',
        description:
          'A clean light mode across the whole app (dark by default in the office, light on the tech view), with a toggle in your profile menu.',
      },
    ],
  },
];

/** The id of the newest release — drives the "unseen" dot on the gift bell. */
export const LATEST_WHATS_NEW_ID: string =
  WHATS_NEW_RELEASES[0]?.id ?? '';

/**
 * Whether the What's New notes pop up automatically the first time a user opens
 * the app after a new release. Currently OFF — What's New lives behind the gift
 * bell (and the Admin tile / menu). Flip to `true` to re-enable the auto-push;
 * the machinery in `WhatsNewAutoPopup` + `useWhatsNew` is still wired up.
 */
export const WHATS_NEW_AUTOPOPUP_ENABLED = false;

/** Human labels + accent intent for each change type. */
export const WHATS_NEW_TYPE_META: Record<
  WhatsNewType,
  { label: string; /** semantic accent used to pick Tailwind classes */ accent: 'emerald' | 'blue' | 'amber' }
> = {
  new: { label: 'New', accent: 'emerald' },
  improved: { label: 'Improved', accent: 'blue' },
  fixed: { label: 'Fixed', accent: 'amber' },
};
